import {
  Contract,
  type Contract as ContractType,
  type ContractTransactionResponse,
  ethers,
  type Listener,
  type Provider,
  type Signer,
} from 'ethers';
import BigNumber from 'bignumber.js';
import { type ContractConfig, MarketType } from '@/common/predictionMarkets/types';
import useAccounts from '@/composables/accounts/useAccounts';
import { ACCOUNT_PROVIDER_ID } from '@/composables/accounts/providers/types';
import ReownEvmAccountProvider from '@/composables/accounts/providers/reownEvmAccountProvider';
import { type FlmFunCore, FlmFunCore__factory } from '@/common/typechain';
import type { Multicall3 } from '@/common/multicall3';
import { Multicall3__factory } from '@/common/multicall3';
import type { LibMarket } from '@/common/typechain/src/contracts/FlmFunCore';
import { createBlob, readBlob, readBlobsBatch } from '@/common/neutraBlob/neutraBlob';
import { logDEBUG, logCacheHit, logCacheMiss, logCacheEviction } from '@/common/debug';
import {
  isValidMarket,
  isValidRawMarket,
  parseUnits,
  formatUnits,
  toBigInt,
  getAddress,
  isAddress,
  parseEther,
  logError,
  logWarn,
} from '@/common/helpers';
import { useEvents, EVENTS } from '@/composables/useEvents';
import {
  getCurrentUserAddress,
  triggerPredictionPlacedCallbacks,
  triggerMarketCreatedCallbacks,
  triggerMarketSettledCallbacks,
  triggerMarketCancelledCallbacks,
  triggerCreatorRegisteredCallbacks,
  triggerCreatorUpdatedCallbacks,
  triggerBalanceRefreshWithDelay,
  triggerTokenApprovedCallbacks,
  triggerTokenTransferredCallbacks,
} from '@/composables/blockchainEventCallbacks';
import { BlockchainEvent } from '@/common/predictionMarkets/events';
import type {
  CreatorCounts,
  CreatorDetails,
  Market,
  MarketListener,
  Outcome,
  OutcomeInput,
  Prediction,
  PredictionWithMarket,
} from '@/common/predictionMarkets/types';
import {
  getDefaultApprovalAmount,
  DEFAULT_APPROVAL_AMOUNT_STRING,
  CREATORS_PAGE_SIZE,
  PREDICTIONS_PAGE_SIZE,
  CREATOR_MARKETS_PAGE_SIZE,
  MARKET_CONTRACT_ADDRESS,
  MULTICALL3_ADDRESS,
  HTTP_RPC_URL,
  WSS_RPC_URL,
  CHAIN_CONFIG,
  CONTRACT_CONFIG_CACHE_DURATION_MS,
  PREDICTION_COUNT_CACHE_DURATION_MS,
  MARKET_CACHE_DURATION_MS,
  MARKET_CACHE_MAX_SIZE,
  BASIS_POINTS_DIVISOR,
  CREATOR_CACHE_DURATION_MS,
  HOMEPAGE_STATS_CACHE_DURATION_MS,
  CREATOR_STATS_CACHE_DURATION_MS,
  CREATOR_COUNTS_CACHE_DURATION_MS,
  EVENT_DEDUPLICATION_WINDOW_MS,
  MULTICALL_BATCH_SIZE,
  WEBSOCKET_RECONNECT_BASE_DELAY_MS,
  WEBSOCKET_RECONNECT_MAX_DELAY_MS,
  WEBSOCKET_RECONNECT_MAX_ATTEMPTS,
  WEBSOCKET_LONG_TERM_RETRY_DELAY_MS,
  HTTP_POLLING_INTERVAL_WITH_WS_MS,
  HTTP_POLLING_INTERVAL_WITHOUT_WS_MS,
  WEBSOCKET_HEALTH_CHECK_INTERVAL_MS,
  WEBSOCKET_KEEPALIVE_INTERVAL_MS,
  ENABLE_WEBSOCKET,
  IS_MAINNET,
} from '@/common/config';
import { ZERO_ADDRESS, ZERO_HASH } from '@/common/helpers';

// ERC20 Contract type with methods
type ERC20Contract = ContractType & {
  decimals: () => Promise<bigint>;
  symbol: () => Promise<string>;
  balanceOf: (address: string) => Promise<bigint>;
  allowance: (owner: string, spender: string) => Promise<bigint>;
  approve: (spender: string, amount: bigint) => Promise<ContractTransactionResponse>;
  transfer: (to: string, amount: bigint) => Promise<ContractTransactionResponse>;
  connect: (signerOrProvider: Signer | Provider | null) => ERC20Contract;
};

// Minimal ERC20 ABI for token interactions
const ERC20_ABI = [
  'function decimals() view returns (uint8)',
  'function symbol() view returns (string)',
  'function balanceOf(address) view returns (uint256)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function approve(address spender, uint256 amount) returns (bool)',
  'function transfer(address to, uint256 amount) returns (bool)',
  'event Transfer(address indexed from, address indexed to, uint256 value)',
  'event Approval(address indexed owner, address indexed spender, uint256 value)',
];

type SmartContractOutcomeDetails = {
  title: string; // bytes32
};

type MarketId = number;

export class UserNotCreatorError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UserNotCreatorError';
  }
}

/**
 * EVM-based prediction market provider implementation using ethers.js v6.
 *
 * **Architecture Overview:**
 * - Dual-provider design: HTTP (reliable, batched) + WebSocket (instant events)
 * - Aggressive multi-layer caching to minimize RPC calls
 * - Event deduplication to prevent duplicate processing
 * - Automatic WebSocket reconnection with exponential backoff
 *
 * **⚠️ SINGLETON PATTERN - DO NOT CREATE NEW INSTANCES!**
 * - Use: `import { evmProvider } from '@/common/predictionMarkets/predictionMarkets'`
 * - Or call exported functions from `predictionMarkets.ts`
 * - Multiple instances create duplicate WebSocket connections and waste resources
 *
 * **Provider Strategy:**
 * - HTTP JsonRpcProvider: Main path for all RPC calls (1s polling when WS down, 10s when WS up)
 * - WebSocket Provider: Instant event notifications (~100ms latency)
 * - Event deduplication: Prevents processing same event twice from both providers
 *
 * **Caching Strategy:**
 * - Market data: TTL-based (5 min default), LRU eviction, max 1000 entries
 * - Token/contract config: Long-lived (15 min)
 * - Creator data: Medium TTL (5 min)
 * - Stats/counts: Short TTL (2 min) for accurate counts
 *
 * **Event Handling:**
 * - CreatedMarket, SettledMarket, PlacedPrediction, CancelledMarket (global)
 * - CreatedCreator, UpdatedCreator (global, all users benefit)
 * - Automatic cache invalidation on relevant events
 * - Callbacks for UI notifications (success popups, balance refresh)
 *
 * @example
 * ```typescript
 * import { evmProvider } from '@/common/predictionMarkets/predictionMarkets';
 *
 * // Fetch markets
 * const markets = await evmProvider.getMarkets();
 *
 * // Subscribe to events
 * await evmProvider.subscribeToMarketUpdates((market) => {
 *   console.log('Market created:', market);
 * });
 * ```
 */
export class EvmPredictionMarketProvider {
  MARKET_ADDRESS: string;

  // ethers v6 - Dual provider setup for reliability:
  // - HTTP provider for all RPC calls (reliable, batching support)
  // - WebSocket provider for instant events (fast, but can drop)
  // - Event deduplication prevents duplicate processing
  private readonly provider: ethers.JsonRpcProvider; // Main provider for RPC calls
  private readonly wsProvider: ethers.WebSocketProvider | null; // Optional for instant events
  private readonly marketContract: FlmFunCore;
  private readonly wsMarketContract: FlmFunCore | null; // Contract connected to WebSocket for events
  private readonly multicall3Contract: Multicall3;
  private tokenContract: ERC20Contract | null = null;
  private wsTokenContract: ERC20Contract | null = null; // Token contract connected to WebSocket for events
  private static instanceCount = 0; // Track constructor calls for debugging

  // Cache for contract config
  private contractConfigCache: {
    data: ContractConfig | null;
    timestamp: number;
  } = { data: null, timestamp: 0 };
  private readonly CONFIG_CACHE_TTL = CONTRACT_CONFIG_CACHE_DURATION_MS;

  // Cache for token decimals and symbol (static = shared across all instances)
  private static tokenDecimalsCache: number | null = null;
  private static tokenSymbolCache: string | null = null;

  // Cache for user predictions count (per userId)
  private predictionCountCache: Map<string, { count: number; timestamp: number }> = new Map();
  private readonly PREDICTION_COUNT_CACHE_TTL = PREDICTION_COUNT_CACHE_DURATION_MS;

  // Cache for market data (per marketId) to avoid refetching when blobs fail
  // Uses LRU eviction when max size is reached
  private marketCache: Map<string, { market: Market; timestamp: number }> = new Map();
  private readonly MARKET_CACHE_TTL = MARKET_CACHE_DURATION_MS;
  private readonly MARKET_CACHE_MAX_SIZE = MARKET_CACHE_MAX_SIZE;

  // Cache for creator list (per page) to avoid refetching on navigation
  private creatorCache: Map<number, { creators: CreatorDetails[]; timestamp: number }> = new Map();
  private readonly CREATOR_CACHE_TTL = CREATOR_CACHE_DURATION_MS;

  // Cache for individual creator details (per creator address) - uses same TTL as creator list cache
  private creatorDetailsCache: Map<string, { details: CreatorDetails; timestamp: number }> = new Map();
  private readonly CREATOR_DETAILS_CACHE_TTL = CREATOR_CACHE_DURATION_MS;

  // Event deduplication - track recent event hashes to prevent duplicate cache clears
  private recentEventHashes: Set<string> = new Set();
  private readonly EVENT_DEDUPLICATION_WINDOW_MS = EVENT_DEDUPLICATION_WINDOW_MS;

  // Store event listeners for proper cleanup
  private createdMarketListener: ((...args: unknown[]) => void | Promise<void>) | null = null;
  private settledMarketListener: ((...args: unknown[]) => void | Promise<void>) | null = null;
  private placedPredictionListener: ((...args: unknown[]) => void | Promise<void>) | null = null;
  private cancelledMarketListener: ((...args: unknown[]) => void | Promise<void>) | null = null;
  private creatorRegisteredListener: ((...args: unknown[]) => void | Promise<void>) | null = null;
  private creatorUpdatedListener: ((...args: unknown[]) => void | Promise<void>) | null = null;
  private tokenApprovedListener: ((...args: unknown[]) => void | Promise<void>) | null = null;
  private tokenTransferredListener: ((...args: unknown[]) => void | Promise<void>) | null = null;
  // Note: Native currency transfers don't emit blockchain events, so cannot be monitored via WebSocket.
  // Block monitoring is too expensive for public RPC endpoints. Rely on transaction confirmation instead.

  // WebSocket reconnection state
  private wsReconnectAttempts: number = 0;
  private wsReconnectTimeout: ReturnType<typeof setTimeout> | null = null;

  // WebSocket connection quality monitoring
  private wsConnectionQuality: 'excellent' | 'good' | 'poor' | 'disconnected' = 'disconnected';
  private wsLastEventTimestamp: number = 0;
  private wsHealthCheckInterval: ReturnType<typeof setInterval> | null = null;
  private wsKeepaliveInterval: ReturnType<typeof setInterval> | null = null;

  // Batch cache clears to avoid duplicate logging within the same tick
  private pendingCreatorStatsClearSet: Set<string> = new Set();
  private pendingCreatorCountsClearSet: Set<string> = new Set();
  private pendingHomePageStatsClear: boolean = false;
  private cacheClearBatchTimeout: number | null = null;

  // Cache for homepage stats (single shared cache for all three counts)
  private homePageStatsCache: {
    creatorsCount?: { count: number; timestamp: number };
    marketsCount?: { count: number; timestamp: number };
    predictionsCount?: { count: number; timestamp: number };
  } = {};
  private readonly HOMEPAGE_STATS_CACHE_TTL = HOMEPAGE_STATS_CACHE_DURATION_MS;

  // In-flight tracking for rules fetching to prevent duplicate requests
  private rulesInFlight: Map<string, Promise<string>> = new Map();

  // Cache for creator address by ID (permanent - never expires, no TTL check needed)
  private creatorAddressByIdCache: Map<string, string> = new Map();

  // Cache for creator stats (per creator address)
  private creatorStatsCache: Map<
    string,
    {
      stats: {
        totalVolume: number;
        totalEarnings: number;
        totalSettledMarkets: number;
        totalPredictionsCount: number;
        lastActivityTimestamp: number;
      };
      timestamp: number;
    }
  > = new Map();
  private readonly CREATOR_STATS_CACHE_TTL = CREATOR_STATS_CACHE_DURATION_MS;

  // Cache for creator counts (all/live/pending/settled markets count per creator address)
  private creatorCountsCache: Map<string, { counts: CreatorCounts; timestamp: number }> = new Map();
  private readonly CREATOR_COUNTS_CACHE_TTL = CREATOR_COUNTS_CACHE_DURATION_MS;

  // Cache for batch active markets count queries (used by getCreatorActiveMarketsCountBatch which is called on HomePage)
  private creatorActiveMarketsCountCache: Map<string, { count: number; timestamp: number }> = new Map();
  private readonly CREATOR_ACTIVE_MARKETS_COUNT_CACHE_TTL = CREATOR_COUNTS_CACHE_DURATION_MS; // Same TTL as counts

  // In-flight request tracking to prevent duplicate concurrent fetches
  private inFlightMarketRequests: Map<string, Promise<Record<string, Market>>> = new Map();
  private inFlightMarketRequestCallerCounts: Map<string, number> = new Map();
  private inFlightCreatorActiveMarketsCountRequests: Map<string, Promise<Record<string, number>>> = new Map();
  private inFlightCreatorActiveMarketsCountCallerCounts: Map<string, number> = new Map();
  private inFlightCreatorStatsRequests: Map<
    string,
    Promise<
      Record<
        string,
        {
          totalVolume: number;
          totalEarnings: number;
          totalSettledMarkets: number;
          totalPredictionsCount: number;
          lastActivityTimestamp: number;
        }
      >
    >
  > = new Map();
  private inFlightCreatorStatsCallerCounts: Map<string, number> = new Map();
  private inFlightContractConfigRequest: Promise<ContractConfig> | null = null;
  private inFlightContractConfigCallerCount = 0;
  private inFlightCreatorDetailsRequests: Map<string, Promise<CreatorDetails>> = new Map();
  private inFlightCreatorDetailsCallerCounts: Map<string, number> = new Map();
  private inFlightCreatorAddressRequests: Map<string, Promise<string>> = new Map();
  private inFlightCreatorAddressCallerCounts: Map<string, number> = new Map();
  private inFlightCreatorCountsRequests: Map<string, Promise<CreatorCounts>> = new Map();
  private inFlightCreatorCountsCallerCounts: Map<string, number> = new Map();
  private inFlightMarketsByCreatorRequests: Map<string, Promise<Market[]>> = new Map();
  private inFlightMarketsByCreatorCallerCounts: Map<string, number> = new Map();
  private inFlightCreatorsCountRequest: Promise<number> | null = null;
  private inFlightCreatorsCountCallerCount = 0;
  private inFlightMarketsCountRequest: Promise<number> | null = null;
  private inFlightMarketsCountCallerCount = 0;
  private inFlightPredictionsCountRequest: Promise<number> | null = null;
  private inFlightPredictionsCountCallerCount = 0;
  private inFlightTokenDecimalsRequest: Promise<number> | null = null;
  private inFlightTokenDecimalsCallerCount = 0;
  private inFlightUserPredictionsCountRequests: Map<string, Promise<number>> = new Map();
  private inFlightUserPredictionsCountCallerCounts: Map<string, number> = new Map();
  private inFlightCreatorsRequests: Map<number, Promise<CreatorDetails[]>> = new Map();
  private inFlightCreatorsCallerCounts: Map<number, number> = new Map();

  /**
   * Initialize the EVM prediction market provider.
   *
   * **⚠️ WARNING: This is called once at app startup. Do not call manually!**
   *
   * Sets up dual-provider architecture:
   * - **HTTP Provider**: JsonRpcProvider with batching (up to 10 requests)
   *   - Polling: 1s when WebSocket down, 10s when WebSocket up
   *   - Used for all RPC calls (getMarketById, getMarkets, etc.)
   *   - Reliable fallback when WebSocket fails
   *
   * - **WebSocket Provider**: For instant event notifications
   *   - ~100ms latency for blockchain events
   *   - Auto-reconnection with exponential backoff (1s→2s→4s→...→30s max)
   *   - After 10 attempts, keeps trying every 60s indefinitely
   *   - Monitors connection health and triggers reconnection on close/error
   *
   * **Contract Connections:**
   * - FlmFunCore (market contract) on both HTTP and WebSocket
   * - Multicall3 for batched reads (HTTP only)
   * - ERC20 token (lazy-loaded when needed)
   *
   * **Instance Tracking:**
   * - Increments static counter to detect accidental multiple instances
   * - Logs warning if multiple instances detected (wastes resources)
   *
   * @throws {Error} If contract addresses are not configured
   */
  constructor() {
    EvmPredictionMarketProvider.instanceCount++;
    const isFirstInstance = EvmPredictionMarketProvider.instanceCount === 1;

    this.MARKET_ADDRESS = MARKET_CONTRACT_ADDRESS;

    // Dual provider setup for maximum reliability:
    // 1. HTTP provider: All RPC calls (reliable, batching, predictable)
    // 2. WebSocket provider: Instant events (<1s latency, can drop)
    // 3. Event deduplication: Prevents duplicate processing
    // 4. Dynamic polling: Slow polling when WebSocket active, fast when disconnected
    const network = ethers.Network.from(CHAIN_CONFIG.chainId);

    // Main HTTP provider for all RPC calls
    this.provider = new ethers.JsonRpcProvider(HTTP_RPC_URL, network, {
      staticNetwork: network,
      batchMaxCount: 10, // Batch up to 10 requests
      pollingInterval: HTTP_POLLING_INTERVAL_WITHOUT_WS_MS, // Default: Poll every 1s (WebSocket will reduce to 10s)
    });

    // Optional WebSocket provider for instant events
    // Skip WebSocket in headless/testing environments (e.g., PageSpeed Insights) to prevent DNS/connection errors
    if (ENABLE_WEBSOCKET && WSS_RPC_URL) {
      try {
        // Double-check WebSocket is available
        if (typeof WebSocket === 'undefined') {
          throw new Error('WebSocket not available in this environment');
        }

        this.wsProvider = new ethers.WebSocketProvider(WSS_RPC_URL, network, {
          staticNetwork: network,
        });
        this.wsMarketContract = FlmFunCore__factory.connect(this.MARKET_ADDRESS, this.wsProvider);

        // Add error handler for WebSocket errors
        this.wsProvider.on('error', (error: unknown) => {
          // Console override in main.ts handles ethers filter error suppression
          logWarn('EvmPredictionMarketProvider', 'websocket provider error', error);
        });

        // Monitor WebSocket connection health (cast to native WebSocket for event listeners)
        const ws = this.wsProvider.websocket as unknown as WebSocket;

        // When WebSocket is healthy, reduce HTTP polling frequency
        ws.addEventListener('open', () => {
          // Slow down HTTP polling when WebSocket is active (optimization)
          this.provider.pollingInterval = HTTP_POLLING_INTERVAL_WITH_WS_MS;
          this.wsConnectionQuality = 'excellent';
          this.startWsHealthCheck();
          this.startWsKeepalive();
          logWarn('EvmPredictionMarketProvider', `websocket connected → HTTP polling reduced to ${HTTP_POLLING_INTERVAL_WITH_WS_MS}ms`);
        });

        ws.addEventListener('close', () => {
          // Restore fast HTTP polling when WebSocket disconnects (fallback)
          this.provider.pollingInterval = HTTP_POLLING_INTERVAL_WITHOUT_WS_MS;
          this.wsConnectionQuality = 'disconnected';
          this.stopWsHealthCheck();
          this.stopWsKeepalive();
          logWarn(
            'EvmPredictionMarketProvider',
            `websocket disconnected → HTTP polling restored to ${HTTP_POLLING_INTERVAL_WITHOUT_WS_MS}ms`,
          );
          // Attempt to reconnect
          this.scheduleWebSocketReconnect();
        });

        ws.addEventListener('error', (event) => {
          // Restore fast HTTP polling on WebSocket error (fallback)
          this.provider.pollingInterval = HTTP_POLLING_INTERVAL_WITHOUT_WS_MS;
          // Only log if not a transient ethers.js error
          if (this.shouldLogError(event)) {
            logWarn(
              'EvmPredictionMarketProvider',
              `websocket error → HTTP polling restored to ${HTTP_POLLING_INTERVAL_WITHOUT_WS_MS}ms`,
              event,
            );
          }
          // Error event is often followed by close, so we'll reconnect on close
        });

        // If WebSocket is already open, reduce polling immediately
        if (ws.readyState === WebSocket.OPEN) {
          this.provider.pollingInterval = HTTP_POLLING_INTERVAL_WITH_WS_MS;
          this.wsReconnectAttempts = 0; // Reset reconnection counter on successful connection
        }

        if (isFirstInstance) {
          logWarn('EvmPredictionMarketProvider', 'dual provider: HTTP (RPC + smart fallback) + WebSocket (instant events)');
        }
      } catch (error) {
        logWarn('EvmPredictionMarketProvider', 'failed to initialize WebSocket, using HTTP polling only', error);
        this.wsProvider = null;
        this.wsMarketContract = null;
      }
    } else {
      this.wsProvider = null;
      this.wsMarketContract = null;
      if (isFirstInstance) {
        const reason = !ENABLE_WEBSOCKET ? 'headless/testing environment detected' : 'no WebSocket configured';
        logWarn('EvmPredictionMarketProvider', `using HTTP provider with 1s polling (${reason})`);
      }
    }

    // Contract connected to HTTP provider for all RPC calls
    this.marketContract = FlmFunCore__factory.connect(this.MARKET_ADDRESS, this.provider);
    this.multicall3Contract = Multicall3__factory.connect(MULTICALL3_ADDRESS, this.provider);

    // Log warning only once when second instance is detected (typically during HMR in development)
    if (EvmPredictionMarketProvider.instanceCount === 2) {
      logDEBUG(`[EvmPredictionMarketProvider] multiple instances detected - this is normal during development HMR (Hot Module Reload)`);
    }
  }

  /**
   * Schedule WebSocket reconnection with exponential backoff
   * First 10 attempts: 1s, 2s, 4s, 8s, 16s, 30s (max)
   * After that: keeps trying every 60s indefinitely (network might recover later)
   */
  private scheduleWebSocketReconnect(): void {
    // Clear any existing reconnect timeout
    if (this.wsReconnectTimeout) {
      clearTimeout(this.wsReconnectTimeout);
    }

    this.wsReconnectAttempts++;

    // Exponential backoff for first N attempts, then long-term retry mode
    let delay: number;
    if (this.wsReconnectAttempts <= WEBSOCKET_RECONNECT_MAX_ATTEMPTS) {
      delay = Math.min(WEBSOCKET_RECONNECT_BASE_DELAY_MS * Math.pow(2, this.wsReconnectAttempts - 1), WEBSOCKET_RECONNECT_MAX_DELAY_MS);
      logWarn(
        'EvmPredictionMarketProvider',
        `scheduling websocket reconnection attempt ${this.wsReconnectAttempts}/${WEBSOCKET_RECONNECT_MAX_ATTEMPTS} in ${delay}ms`,
      );
    } else {
      // Long-term retry mode: keep trying indefinitely
      delay = WEBSOCKET_LONG_TERM_RETRY_DELAY_MS;
      logWarn(
        'EvmPredictionMarketProvider',
        `websocket still disconnected, retrying in ${delay / 1000}s (attempt ${this.wsReconnectAttempts})`,
      );
    }

    this.wsReconnectTimeout = setTimeout(() => {
      this.attemptWebSocketReconnect();
    }, delay);
  }

  /**
   * Attempt to recreate WebSocket connection and re-subscribe to events
   */
  private async attemptWebSocketReconnect(): Promise<void> {
    if (!ENABLE_WEBSOCKET || !WSS_RPC_URL) return;

    logWarn('EvmPredictionMarketProvider', `attempting websocket reconnection (attempt ${this.wsReconnectAttempts})`);

    try {
      const network = ethers.Network.from(CHAIN_CONFIG.chainId);

      // Unsubscribe from all WebSocket events before destroying the provider
      // This prevents "provider destroyed; cancelled request" errors
      if (this.wsProvider) {
        this.unsubscribeWebSocketEvents();
      }

      // Destroy old WebSocket provider if it exists
      if (this.wsProvider) {
        try {
          await this.wsProvider.destroy();
        } catch {
          // Ignore errors during cleanup
        }
      }

      // Create new WebSocket provider
      const newWsProvider = new ethers.WebSocketProvider(WSS_RPC_URL, network, {
        staticNetwork: network,
      });
      const newWsMarketContract = FlmFunCore__factory.connect(this.MARKET_ADDRESS, newWsProvider);

      // Monitor new WebSocket connection health
      const ws = newWsProvider.websocket as unknown as WebSocket;

      ws.addEventListener('open', () => {
        // Success! Reset reconnection counter
        this.wsReconnectAttempts = 0;
        this.provider.pollingInterval = HTTP_POLLING_INTERVAL_WITH_WS_MS;
        this.wsConnectionQuality = 'excellent';
        this.startWsHealthCheck();
        this.startWsKeepalive();
        logWarn(
          'EvmPredictionMarketProvider',
          `websocket reconnected successfully → HTTP polling reduced to ${HTTP_POLLING_INTERVAL_WITH_WS_MS}ms`,
        );

        // Update references to new provider (cast through unknown to access private properties)
        (this as unknown as { wsProvider: ethers.WebSocketProvider | null }).wsProvider = newWsProvider;
        (this as unknown as { wsMarketContract: FlmFunCore | null }).wsMarketContract = newWsMarketContract;

        // Re-subscribe to events with new WebSocket contract
        this.resubscribeWebSocketEvents();
      });

      ws.addEventListener('close', () => {
        this.provider.pollingInterval = HTTP_POLLING_INTERVAL_WITHOUT_WS_MS;
        this.wsConnectionQuality = 'disconnected';
        this.stopWsHealthCheck();
        this.stopWsKeepalive();
        logWarn(
          'EvmPredictionMarketProvider',
          `websocket disconnected → HTTP polling restored to ${HTTP_POLLING_INTERVAL_WITHOUT_WS_MS}ms`,
        );
        this.scheduleWebSocketReconnect();
      });

      ws.addEventListener('error', (event) => {
        this.provider.pollingInterval = HTTP_POLLING_INTERVAL_WITHOUT_WS_MS;
        // Only log if not a transient ethers.js error
        if (this.shouldLogError(event)) {
          logWarn(
            'EvmPredictionMarketProvider',
            `websocket error → HTTP polling restored to ${HTTP_POLLING_INTERVAL_WITHOUT_WS_MS}ms`,
            event,
          );
        }
        // Error event is often followed by close, so we'll reconnect on close
      });
    } catch (error) {
      logWarn('EvmPredictionMarketProvider', `websocket reconnection attempt ${this.wsReconnectAttempts} failed`, error);
      // Schedule next reconnection attempt
      this.scheduleWebSocketReconnect();
    }
  }

  /**
   * Re-subscribe to WebSocket events after reconnection
   * Uses the same listeners that were created during initial subscription
   */
  private resubscribeWebSocketEvents(): void {
    if (!this.wsMarketContract) return;

    logWarn('EvmPredictionMarketProvider', 're-subscribing to websocket events after reconnection');

    try {
      const wsCreatedMarketEvent = this.wsMarketContract.filters.CreatedMarket;
      const wsSettledMarketEvent = this.wsMarketContract.filters.SettledMarket;
      const wsPlacedPredictionEvent = this.wsMarketContract.filters.PlacedPrediction;
      const wsCancelledMarketEvent = this.wsMarketContract.filters.CancelledMarket;
      const wsCreatedCreatorEvent = this.wsMarketContract.filters.CreatedCreator;
      const wsUpdatedCreatorEvent = this.wsMarketContract.filters.UpdatedCreator;
      const rawUserAddrForResubscribe = getCurrentUserAddress();
      const currentUserAddrForFilter = rawUserAddrForResubscribe && isAddress(rawUserAddrForResubscribe) ? rawUserAddrForResubscribe : null;
      const wsApprovedEvent =
        currentUserAddrForFilter && this.wsTokenContract
          ? this.wsTokenContract.filters.Approval!(getAddress(currentUserAddrForFilter))
          : null;
      const wsTransferredEvent = IS_MAINNET ? null : this.wsTokenContract?.filters.Transfer;

      if (this.createdMarketListener) {
        this.wsMarketContract.on(wsCreatedMarketEvent, this.createdMarketListener);
      }
      if (this.settledMarketListener) {
        this.wsMarketContract.on(wsSettledMarketEvent, this.settledMarketListener);
      }
      if (this.placedPredictionListener) {
        this.wsMarketContract.on(wsPlacedPredictionEvent, this.placedPredictionListener);
      }
      if (this.cancelledMarketListener) {
        this.wsMarketContract.on(wsCancelledMarketEvent, this.cancelledMarketListener);
      }
      if (this.creatorRegisteredListener) {
        this.wsMarketContract.on(wsCreatedCreatorEvent, this.creatorRegisteredListener);
      }
      if (this.creatorUpdatedListener) {
        this.wsMarketContract.on(wsUpdatedCreatorEvent, this.creatorUpdatedListener);
      }
      // Re-subscribe to token events if WebSocket token contract exists
      if (this.wsTokenContract && this.tokenApprovedListener && wsApprovedEvent) {
        this.wsTokenContract.on(wsApprovedEvent, this.tokenApprovedListener);
      }
      if (this.wsTokenContract && this.tokenTransferredListener && wsTransferredEvent) {
        this.wsTokenContract.on(wsTransferredEvent, this.tokenTransferredListener);
      }

      logWarn('EvmPredictionMarketProvider', 'websocket event re-subscription complete');
    } catch (error) {
      // Provider destroyed - this is expected when disconnecting, silently ignore
      if ((error as Error)?.message?.includes('provider destroyed')) {
        logWarn('EvmPredictionMarketProvider', 'Cannot resubscribe - provider destroyed (expected during disconnect)');
        return;
      }
      // Other errors - log but don't throw
      logWarn('EvmPredictionMarketProvider', 'Failed to resubscribe to websocket events', error);
    }
  }

  /**
   * Unsubscribe from all WebSocket events before destroying the provider
   * This prevents "provider destroyed; cancelled request" errors
   */
  private unsubscribeWebSocketEvents(): void {
    if (!this.wsMarketContract && !this.wsTokenContract) return;

    try {
      // Remove all market event listeners
      if (this.wsMarketContract) {
        this.wsMarketContract.removeAllListeners();
      }

      // Remove all token event listeners
      if (this.wsTokenContract) {
        this.wsTokenContract.removeAllListeners();
      }

      logDEBUG('[EvmPredictionMarketProvider] websocket events unsubscribed');
    } catch (error) {
      // Ignore errors during cleanup
      logDEBUG('[EvmPredictionMarketProvider] error unsubscribing websocket events (ignored):', error);
    }
  }

  /**
   * Ensures address is checksummed to prevent ENS resolution attempts by ethers.js
   * Checksummed addresses are recognized as addresses, not ENS names
   */
  private ensureChecksummedAddress(address: string): string {
    if (!/^0x[0-9a-fA-F]{40}$/.test(address)) {
      throw new Error(`Invalid address format: ${address}`);
    }
    return getAddress(address);
  }

  /**
   * Validates event data to prevent processing invalid/uninitialized blockchain events
   *
   * Checks for common invalid patterns:
   * - Market ID 0 (placeholder/initialization value) - if marketId provided
   * - Zero address actors (uninitialized or corrupted data)
   *
   * @param marketIdOrAddress - Either market ID (bigint) or address (string) for creator events
   * @param actorAddress - Optional address of actor (creator, settler, predictor, etc.) - omit for creator-only events
   * @param eventName - Name of event for logging purposes
   * @returns true if data is valid, false if should be skipped
   */
  private isValidEventData(marketIdOrAddress: bigint | string, actorAddress: string | undefined, eventName: string): boolean {
    // Case 1: Market events (marketId + actor address)
    if (typeof marketIdOrAddress === 'bigint') {
      if (marketIdOrAddress === 0n || !actorAddress || actorAddress === ZERO_ADDRESS) {
        logDEBUG(`[EvmPredictionMarketProvider] Skipping invalid ${eventName} event:`, {
          marketId: marketIdOrAddress.toString(),
          actor: actorAddress,
        });
        return false;
      }
    }
    // Case 2: Creator events (only actor address, marketIdOrAddress is the address)
    else if (typeof marketIdOrAddress === 'string') {
      if (!marketIdOrAddress || marketIdOrAddress === ZERO_ADDRESS) {
        logDEBUG(`[EvmPredictionMarketProvider] Skipping invalid ${eventName} event:`, {
          wallet: marketIdOrAddress,
        });
        return false;
      }
    }
    return true;
  }

  /**
   * Get the current user's address for filtering predictions
   * Returns ZeroAddress if no user is logged in or if using play account (will return empty predictions)
   *
   * NOTE: Currently unused. We fetch all predictions to correctly calculate settlement/creator fees.
   * User-specific predictions are accessed via market.userIdToPredictions[userId] after fetching.
   * Could be re-enabled if we implement dual-fetch strategy or contract-side settlement calculation.
   */
  // private getCurrentUserAddressForFilter(): string {
  //   const accounts = useAccounts();
  //   const address = accounts.selectedAccount.value?.address;

  //   // Play account doesn't have a real blockchain address, so treat it as no user
  //   if (!address || address === PLAY_ACCOUNT_ADDRESS) {
  //     return ZERO_ADDRESS;
  //   }

  //   return address;
  // }

  /**
   * Get the token contract instance, lazily initializing it with the correct address from config
   */
  private async getTokenContract(): Promise<ERC20Contract> {
    if (!this.tokenContract) {
      const config = await this.getContractConfig();
      this.tokenContract = new Contract(config.predictionCurrency, ERC20_ABI, this.provider) as ERC20Contract;
    }
    return this.tokenContract;
  }

  /**
   * Get WebSocket token contract (lazy initialization)
   * Only available if WebSocket provider exists
   */
  private async getWsTokenContract(): Promise<ERC20Contract | null> {
    if (!this.wsProvider) {
      return null;
    }
    if (!this.wsTokenContract) {
      const config = await this.getContractConfig();
      this.wsTokenContract = new Contract(config.predictionCurrency, ERC20_ABI, this.wsProvider) as ERC20Contract;
    }
    return this.wsTokenContract;
  }

  /**
   * Get the decimals of the prediction currency token
   */
  async getTokenDecimals(): Promise<number> {
    if (EvmPredictionMarketProvider.tokenDecimalsCache !== null) {
      logDEBUG('getTokenDecimals', 'CACHED', EvmPredictionMarketProvider.tokenDecimalsCache);
      logCacheHit('tokenDecimals');
      return EvmPredictionMarketProvider.tokenDecimalsCache;
    }

    // Check if request is already in flight
    if (this.inFlightTokenDecimalsRequest) {
      this.inFlightTokenDecimalsCallerCount++;
      return this.inFlightTokenDecimalsRequest;
    }

    logDEBUG('getTokenDecimals', 'fetching...');
    logCacheMiss('tokenDecimals');
    this.inFlightTokenDecimalsCallerCount = 1;

    const fetchPromise = (async () => {
      try {
        const tokenContract = await this.getTokenContract();
        const decimals = Number(await tokenContract.decimals());
        EvmPredictionMarketProvider.tokenDecimalsCache = Number(decimals);
        return EvmPredictionMarketProvider.tokenDecimalsCache;
      } finally {
        const callerCount = this.inFlightTokenDecimalsCallerCount;
        if (callerCount > 1) {
          logDEBUG(`[IN-FLIGHT DEDUP]`, `${callerCount} concurrent callers reused request for token decimals`);
        }
        this.inFlightTokenDecimalsRequest = null;
        this.inFlightTokenDecimalsCallerCount = 0;
      }
    })();

    this.inFlightTokenDecimalsRequest = fetchPromise;
    return fetchPromise;
  }

  /**
   * Filters out noisy errors before logging.
   * Note: Console override in main.ts handles ethers filter error suppression.
   *
   * @param error - Error object to check
   * @returns true if error should be logged, false if it should be suppressed
   */
  private shouldLogError(error: unknown): boolean {
    const errorMessage = String(error);
    // Suppress 'from block not found' errors (blocks may not exist yet on testnet)
    if (errorMessage.includes('from block not found')) {
      return false;
    }
    // All other ethers filter errors (@TODO, coalesce, etc.) are handled by console override
    return true;
  }

  /**
   * Start monitoring WebSocket connection quality based on event activity
   */
  private startWsHealthCheck(): void {
    if (this.wsHealthCheckInterval) return;

    // Initialize timestamp if not set
    if (this.wsLastEventTimestamp === 0) {
      this.wsLastEventTimestamp = Date.now();
    }

    this.wsHealthCheckInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastEvent = this.wsLastEventTimestamp > 0 ? now - this.wsLastEventTimestamp : 0;

      if (!this.wsProvider || this.wsProvider.websocket.readyState === WebSocket.CLOSED) {
        this.wsConnectionQuality = 'disconnected';
      } else if (timeSinceLastEvent === 0 || timeSinceLastEvent < 30000) {
        this.wsConnectionQuality = 'excellent'; // Recent activity or just started
      } else if (timeSinceLastEvent < 120000) {
        this.wsConnectionQuality = 'good'; // Some delay but acceptable
      } else {
        this.wsConnectionQuality = 'poor'; // No events for 2+ minutes
      }

      logDEBUG(`[WS Quality] ${this.wsConnectionQuality} - Last event: ${Math.floor(timeSinceLastEvent / 1000)}s ago`);
    }, WEBSOCKET_HEALTH_CHECK_INTERVAL_MS);
  }

  /**
   * Stop monitoring WebSocket connection quality
   */
  private stopWsHealthCheck(): void {
    if (this.wsHealthCheckInterval) {
      clearInterval(this.wsHealthCheckInterval);
      this.wsHealthCheckInterval = null;
    }
  }

  /**
   * Start WebSocket keepalive mechanism to prevent server timeout on idle connections
   * Sends periodic lightweight queries (block number) to keep connection active
   */
  private startWsKeepalive(): void {
    if (this.wsKeepaliveInterval) return;
    if (!this.wsProvider) return;

    this.wsKeepaliveInterval = setInterval(async () => {
      if (!this.wsProvider || this.wsProvider.websocket.readyState !== WebSocket.OPEN) return;

      try {
        // Send lightweight query to keep connection alive
        await this.wsProvider.getBlockNumber();
        logDEBUG('[WS Keepalive] Ping sent successfully');
      } catch (error) {
        logDEBUG('[WS Keepalive] Ping failed:', error);
        // Connection will be handled by error/close handlers
      }
    }, WEBSOCKET_KEEPALIVE_INTERVAL_MS);
  }

  /**
   * Stop WebSocket keepalive mechanism
   */
  private stopWsKeepalive(): void {
    if (this.wsKeepaliveInterval) {
      clearInterval(this.wsKeepaliveInterval);
      this.wsKeepaliveInterval = null;
    }
  }

  /**
   * Get current WebSocket connection quality
   */
  getWsConnectionQuality(): 'excellent' | 'good' | 'poor' | 'disconnected' {
    return this.wsConnectionQuality;
  }

  /**
   * Fetch a single market by ID with aggressive caching.
   *
   * Uses multi-layer caching:
   * - In-memory cache: TTL 5 minutes, LRU eviction (max 1000 entries)
   * - Automatically cleared on relevant blockchain events
   *
   * Supports smart provider selection:
   * - 'http' (default): HTTP provider with Multicall3
   * - 'ws': WebSocket provider (instant for event-driven queries)
   * - 'auto': Try WebSocket first, fallback to HTTP
   *
   * @param marketId - Market ID as string (must be valid uint32)
   * @param preferProvider - Provider selection mode ('http' | 'ws' | 'auto')
   * @returns Promise resolving to Market object
   * @throws {Error} If marketId is invalid or market not found
   *
   * @example
   * ```typescript
   * // Standard fetch (HTTP, default)
   * const market = await evmProvider.getMarketById('123');
   *
   * // Event-driven fetch (instant via WebSocket)
   * const market = await evmProvider.getMarketById('123', 'auto');
   * console.log(`Market: ${market.question} (${market.outcomes.length} outcomes)`);
   * ```
   */
  async getMarketById(marketId: string, preferProvider: 'http' | 'ws' | 'auto' = 'http'): Promise<Market> {
    const id = Number(marketId);
    if (!Number.isInteger(id) || id < 0 || id > 0xffffffff) {
      throw new Error('marketId must be a uint32');
    }

    // Use getMarketsByIds to leverage caching
    const markets = await this.getMarketsByIds([marketId], preferProvider);
    const market = markets[marketId];

    if (!market) {
      throw new Error(`Market ${marketId} not found`);
    }

    return market;
  }

  /**
   * Fetch rules for a specific market on-demand (lazy loading)
   * This is called when user clicks "View Rules" to avoid blocking initial market display
   * Includes in-flight tracking to prevent duplicate requests
   */
  async getMarketRules(marketId: string): Promise<string> {
    // Check if there's already an in-flight request for this market
    const existingRequest = this.rulesInFlight.get(marketId);
    if (existingRequest) {
      logDEBUG('getMarketRules', `Reusing in-flight request for market ${marketId}`);
      return existingRequest;
    }

    const id = Number(marketId);
    if (!Number.isInteger(id) || id < 0 || id > 0xffffffff) {
      throw new Error('marketId must be a uint32');
    }

    logDEBUG('getMarketRules', `Fetching rules for market ${marketId}`);

    // Create and track the request
    const requestPromise = (async () => {
      try {
        // Fetch raw market data from contract (using burn address = minimal predictions data)
        const rawMarket = await this.marketContract.getMarket(id, '0x000000000000000000000000000000000000dEaD');

        // Validate raw market data early (skip invalid/uninitialized markets)
        if (!isValidRawMarket(rawMarket)) {
          logWarn(
            'EvmPredictionMarketProvider',
            `skipping rules fetch for invalid market ${marketId}: rawId='${rawMarket.id}', creator='${rawMarket.creator}'`,
          );
          return '';
        }

        // Extract blob ID from description field
        const rulesBlobId = rawMarket.description.slice(2);

        if (!rulesBlobId || rulesBlobId === '') {
          return '';
        }

        // Fetch rules from blob storage
        const rules = await readBlob(rulesBlobId);
        return rules || '';
      } catch (error) {
        logWarn('EvmPredictionMarketProvider', `error fetching rules for market ${marketId}`, error);
        return 'Important: Failed to load rules! Rules may still apply but they could not be loaded.';
      } finally {
        // Clean up in-flight tracking when done
        this.rulesInFlight.delete(marketId);
      }
    })();

    this.rulesInFlight.set(marketId, requestPromise);
    return requestPromise;
  }

  /**
   * Batch fetch rules for multiple markets efficiently
   * Uses readBlobsBatch for parallel blob fetching
   * @param markets Array of markets to fetch rules for
   * @returns Promise that resolves when all rules are fetched and markets are updated
   */
  async batchFetchMarketRules(markets: Market[]): Promise<void> {
    if (markets.length === 0) return;

    // Filter markets that need rules (empty rules and have IDs)
    const marketsNeedingRules = markets.filter((m) => m.rules === '' && m.id);
    if (marketsNeedingRules.length === 0) return;

    logDEBUG('batchFetchMarketRules', `Batch fetching rules for ${marketsNeedingRules.length} markets`);

    try {
      // Extract blob IDs from markets
      // Need to fetch raw market data to get blob IDs (description field)
      const marketIds = marketsNeedingRules.map((m) => Number(m.id));

      // Chunk markets to avoid RPC/gas limits
      const marketIdToBlobId = new Map<string, string>();
      const allBlobIds: string[] = [];

      for (let i = 0; i < marketIds.length; i += MULTICALL_BATCH_SIZE) {
        const chunkIds = marketIds.slice(i, i + MULTICALL_BATCH_SIZE);
        const chunkMarkets = marketsNeedingRules.slice(i, i + MULTICALL_BATCH_SIZE);

        // Fetch raw markets using Multicall3 for efficiency
        const calls = chunkIds.map((id) => ({
          target: this.MARKET_ADDRESS,
          allowFailure: false,
          callData: this.marketContract.interface.encodeFunctionData('getMarket', [id, '0x000000000000000000000000000000000000dEaD']),
        }));

        const results = await this.multicall3Contract.aggregate3.staticCall(calls);

        // Extract blob IDs from this chunk
        for (let j = 0; j < results.length; j++) {
          const result = results[j];
          if (!result?.success) continue;

          const rawMarket = this.marketContract.interface.decodeFunctionResult('getMarket', result.returnData)[0];

          // Validate raw market data early (skip invalid/uninitialized markets)
          if (!isValidRawMarket(rawMarket)) {
            logWarn(
              'EvmPredictionMarketProvider',
              `skipping rules fetch for invalid batch market: rawId='${rawMarket.id}', creator='${rawMarket.creator}'`,
            );
            continue;
          }

          const blobId = rawMarket.description.slice(2);

          if (blobId && blobId !== '') {
            allBlobIds.push(blobId);
            marketIdToBlobId.set(chunkMarkets[j]?.id || '', blobId);
          }
        }
      }

      // Batch fetch all blobs
      const blobResults = await readBlobsBatch(allBlobIds);

      // Update markets with fetched rules
      for (const market of marketsNeedingRules) {
        const blobId = marketIdToBlobId.get(market.id);
        if (blobId) {
          market.rules = blobResults.get(blobId) || '';
        }
      }

      logDEBUG('batchFetchMarketRules', `Successfully fetched ${allBlobIds.length} rules`);
    } catch (error) {
      logWarn('EvmPredictionMarketProvider', 'error batch fetching market rules', error);
      // Silent failure - individual lazy loads will handle errors
    }
  }

  /**
   * Batch fetch multiple markets using Multicall3 for efficiency.
   *
   * Supports smart provider selection for optimized fetching:
   * - 'http': Uses HTTP provider with Multicall3 (optimal for batch queries, default)
   * - 'ws': Uses WebSocket provider exclusively (instant for event-driven queries)
   * - 'auto': Tries WebSocket first, falls back to HTTP on error (best for events)
   *
   * Features:
   * - In-flight request deduplication (shares promises across concurrent callers)
   * - Provider-agnostic caching (single cache map, last-write-wins)
   * - LRU eviction (max 1000 markets)
   * - 5-minute TTL, cleared on blockchain events
   *
   * @param marketIds - Array of market IDs to fetch
   * @param preferProvider - Provider selection mode ('http' | 'ws' | 'auto')
   * @returns Record mapping market IDs to Market objects
   *
   * @example
   * ```typescript
   * // Event-driven fetch (instant via WebSocket)
   * const markets = await provider.getMarketsByIds(['123', '124'], 'auto');
   *
   * // Batch fetch (efficient via HTTP Multicall3)
   * const markets = await provider.getMarketsByIds(['123', '124', '125']);
   * ```
   */
  async getMarketsByIds(marketIds: string[], preferProvider: 'http' | 'ws' | 'auto' = 'http'): Promise<Record<string, Market>> {
    if (marketIds.length === 0) {
      return {};
    }

    // Create a sorted, normalized key for deduplication (ignore provider for max dedup)
    const requestKey = [...marketIds].sort().join(',');

    // Check if this exact request is already in flight
    const inFlightRequest = this.inFlightMarketRequests.get(requestKey);
    if (inFlightRequest) {
      // Track concurrent caller count for logging
      // Note: Ignoring provider preference when reusing in-flight request (max deduplication)
      const currentCount = this.inFlightMarketRequestCallerCounts.get(requestKey) || 1;
      this.inFlightMarketRequestCallerCounts.set(requestKey, currentCount + 1);
      return inFlightRequest;
    }

    // Log once when creating the request
    logDEBUG('getMarketsByIds', marketIds.length, 'markets', preferProvider !== 'http' ? `(prefer: ${preferProvider})` : '');

    // Initialize caller count (1 = the original caller who created the request)
    this.inFlightMarketRequestCallerCounts.set(requestKey, 1);

    // Create the fetch promise
    const fetchPromise = this._getMarketsByIdsImpl(marketIds, preferProvider);

    // Store it to prevent duplicate requests
    this.inFlightMarketRequests.set(requestKey, fetchPromise);

    // Clean up after completion (success or failure)
    fetchPromise.finally(() => {
      // Log if multiple callers reused this request (in-flight deduplication working)
      const callerCount = this.inFlightMarketRequestCallerCounts.get(requestKey) || 1;
      if (callerCount > 1) {
        logDEBUG(`[IN-FLIGHT DEDUP]`, `${callerCount} concurrent callers reused request for markets:`, marketIds);
      }
      this.inFlightMarketRequests.delete(requestKey);
      this.inFlightMarketRequestCallerCounts.delete(requestKey);
    });

    return fetchPromise;
  }

  /**
   * Internal implementation for fetching markets by IDs with provider selection.
   *
   * Routes to appropriate fetch method based on preferProvider:
   * - 'ws' mode → _fetchMarketsViaWebSocket() (throws on error)
   * - 'auto' mode → Try WS first, fallback to HTTP on error
   * - 'http' mode → HTTP Multicall3 (default, optimal for batches)
   *
   * HTTP path uses Multicall3 for efficient batching. WS path fetches individually
   * but is instant for event-driven queries (eliminates race condition).
   *
   * @param marketIds - Array of market IDs to fetch
   * @param preferProvider - Provider selection mode
   * @returns Record mapping market IDs to Market objects
   * @private
   */
  private async _getMarketsByIdsImpl(
    marketIds: string[],
    preferProvider: 'http' | 'ws' | 'auto' = 'http',
  ): Promise<Record<string, Market>> {
    const now = Date.now();
    const marketMap: Record<string, Market> = {};
    const uncachedMarketIds: string[] = [];

    // Check cache first
    for (const marketId of marketIds) {
      const cached = this.marketCache.get(marketId);
      if (cached && now - cached.timestamp < this.MARKET_CACHE_TTL) {
        marketMap[marketId] = cached.market;
        // Touch the entry to mark it as recently used (LRU)
        this.marketCache.delete(marketId);
        this.marketCache.set(marketId, cached);
      } else {
        uncachedMarketIds.push(marketId);
      }
    }

    // If all markets were cached, return immediately
    if (uncachedMarketIds.length === 0) {
      logDEBUG('getMarketsByIds', `CACHED ${marketIds.length} markets`);
      logCacheHit('market');
      return marketMap;
    }

    // Log cache hits/misses
    if (marketIds.length - uncachedMarketIds.length > 0) {
      logCacheHit('market');
    }
    if (uncachedMarketIds.length > 0) {
      logCacheMiss('market');
    }

    // Log partial cache hit if some markets were cached
    const cachedCount = marketIds.length - uncachedMarketIds.length;
    if (cachedCount > 0) {
      logDEBUG('getMarketsByIds', `${cachedCount} cached, fetching ${uncachedMarketIds.length}`);
    }

    // Determine which provider to use
    let useWs = false;
    if (preferProvider === 'ws' && this.wsMarketContract) {
      useWs = true;
    } else if (preferProvider === 'auto' && this.wsMarketContract) {
      useWs = true; // Try WS first in auto mode
    }

    // Route to appropriate fetch method
    if (useWs) {
      try {
        // WebSocket path - fetch individually (instant for event-driven queries)
        const wsResults = await this._fetchMarketsViaWebSocket(uncachedMarketIds, now);
        return { ...marketMap, ...wsResults };
      } catch (error) {
        // If 'auto' mode, fall back to HTTP on WS failure
        if (preferProvider === 'auto') {
          logWarn('EvmPredictionMarketProvider', '[Provider] WebSocket fetch failed in auto mode, falling back to HTTP', error);
          // Continue to HTTP path below
        } else {
          // 'ws' mode - propagate the error
          throw error;
        }
      }
    }

    // HTTP path with Multicall3 (optimal for batch queries)
    // Validate uncached market IDs
    for (const marketId of uncachedMarketIds) {
      const id = Number(marketId);
      if (!Number.isInteger(id) || id < 0 || id > 0xffffffff) {
        throw new Error(`marketId must be a uint32, got: ${marketId}`);
      }
    }

    // Prepare multicall calls for uncached markets
    // IMPORTANT: Pass ZERO_ADDRESS to get ALL predictions for correct settlement/creator fees calculation
    // User-specific predictions are fetched separately via getMarketById() when needed
    const calls = uncachedMarketIds.map((marketId) => {
      const callData = this.marketContract.interface.encodeFunctionData('getMarket', [Number(marketId), ZERO_ADDRESS]);
      return {
        target: this.MARKET_ADDRESS,
        allowFailure: false,
        callData,
      };
    });

    // Execute multicall
    const results = await this.multicall3Contract.aggregate3.staticCall(calls);

    // Parse results and decode all raw markets first
    const rawMarkets: Array<{ marketId: string; rawMarket: LibMarket.MarketStructOutput }> = [];

    for (let i = 0; i < results.length; i++) {
      const result = results[i];
      const marketId = uncachedMarketIds[i] as string;

      if (result?.success) {
        const decodedData = this.marketContract.interface.decodeFunctionResult('getMarket', result.returnData);
        const rawMarket = decodedData[0] as LibMarket.MarketStructOutput;

        // Validate raw market data early (skip invalid/uninitialized markets)
        if (!isValidRawMarket(rawMarket)) {
          logWarn(
            'EvmPredictionMarketProvider',
            `skipping invalid raw market data for ID ${marketId}: rawId='${rawMarket.id}', creator='${rawMarket.creator}'`,
          );
          continue;
        }

        rawMarkets.push({ marketId, rawMarket });
      } else {
        logWarn('EvmPredictionMarketProvider', `failed to fetch market ${marketId} in multicall`);
      }
    }

    // Extract all blob IDs from raw markets and batch fetch them in parallel
    const blobIds = rawMarkets.map(({ rawMarket }) => rawMarket.description.slice(2)).filter((id) => id !== '');

    // Skip blob fetching for faster market display - rules are loaded lazily when user clicks "View Rules"
    // This saves ~0.5s per market creation, making markets appear instantly
    logDEBUG('getMarketsByIds', `Skipping ${blobIds.length} rule blobs (lazy load on demand)`);
    const blobCache = new Map<string, string>(); // Empty cache - rules will be empty strings

    // Convert all markets in parallel (rules will be empty, loaded lazily later)
    const conversionPromises = rawMarkets.map(async ({ marketId, rawMarket }) => {
      try {
        const market = await this.convertRawMarketToMarket(rawMarket, blobCache);

        // Validate market data (check for uninitialized state)
        if (!isValidMarket(market)) {
          logWarn(
            'EvmPredictionMarketProvider',
            `skipping invalid market data for ID ${marketId}: id='${market.id}', creator='${market.creatorId}'`,
          );
          return null;
        }

        return { marketId, market };
      } catch (error) {
        logWarn('EvmPredictionMarketProvider', `failed to convert market ${marketId}`, error);
        return null;
      }
    });

    const convertedMarkets = await Promise.all(conversionPromises);

    // Build the final map and update cache
    for (const result of convertedMarkets) {
      if (result) {
        marketMap[result.marketId] = result.market;
        // Cache the market for future requests (with LRU eviction)
        this.setMarketCache(result.marketId, result.market, now);
      }
    }

    return marketMap;
  }

  /**
   * Fetch markets via WebSocket provider for instant event-driven queries.
   *
   * **Why WebSocket is faster for events:**
   * The WebSocket provider that emitted the event MUST have the market data
   * (proof = event emission). Querying the same node eliminates the 100-600ms
   * RPC propagation lag that affects HTTP providers.
   *
   * **Fetching strategy:**
   * - Fetches markets concurrently via Promise.all (not batched)
   * - Validates data (checks for marketId='0' or creator='0x0000...')
   * - Updates shared cache (provider-agnostic)
   * - Throws error if WebSocket unavailable (for auto-mode fallback)
   *
   * @param marketIds - Array of market IDs to fetch
   * @param timestamp - Cache timestamp for consistent cache entries
   * @returns Record mapping market IDs to Market objects
   * @throws Error if WebSocket provider not available
   * @private
   *
   * @example
   * ```typescript
   * // Called internally by _getMarketsByIdsImpl in 'ws' or 'auto' mode
   * const markets = await this._fetchMarketsViaWebSocket(['123'], Date.now());
   * ```
   */
  private async _fetchMarketsViaWebSocket(marketIds: string[], timestamp: number): Promise<Record<string, Market>> {
    const marketMap: Record<string, Market> = {};

    if (!this.wsMarketContract) {
      throw new Error('WebSocket provider not available');
    }

    try {
      // Fetch markets concurrently via WebSocket
      logDEBUG('_fetchMarketsViaWebSocket', `Fetching ${marketIds.length} markets via WS`);

      // IMPORTANT: Pass ZERO_ADDRESS to get ALL predictions for correct settlement/creator fees calculation
      // User-specific predictions are fetched separately via getMarketById() when needed

      const fetchPromises = marketIds.map(async (marketId) => {
        try {
          const rawMarket = await this.wsMarketContract!.getMarket(marketId, ZERO_ADDRESS);

          // Validate raw market data early (skip invalid/uninitialized markets)
          if (!isValidRawMarket(rawMarket)) {
            logWarn(
              'EvmPredictionMarketProvider',
              `[WS] Skipping invalid raw market data for ID ${marketId}: rawId='${rawMarket.id}', creator='${rawMarket.creator}'`,
            );
            return { marketId, rawMarket: null };
          }

          return { marketId, rawMarket };
        } catch (error) {
          logWarn('EvmPredictionMarketProvider', `[WS] Failed to fetch market ${marketId}`, error);
          return { marketId, rawMarket: null };
        }
      });

      const results = await Promise.all(fetchPromises);

      // Convert raw markets to Market objects
      const conversionPromises = results.map(async ({ marketId, rawMarket }) => {
        if (!rawMarket) return null;

        try {
          const market = await this.convertRawMarketToMarket(rawMarket);

          // Validate market data (check for uninitialized state)
          if (!isValidMarket(market)) {
            logWarn(
              'EvmPredictionMarketProvider',
              `[WS] Skipping invalid market data for ID ${marketId}: id='${market.id}', creator='${market.creatorId}'`,
            );
            return null;
          }

          return { marketId, market };
        } catch (error) {
          logWarn('EvmPredictionMarketProvider', `[WS] Failed to convert market ${marketId}`, error);
          return null;
        }
      });

      const convertedMarkets = await Promise.all(conversionPromises);

      // Build the final map and update cache
      for (const result of convertedMarkets) {
        if (result) {
          marketMap[result.marketId] = result.market;
          // Cache the market for future requests
          this.setMarketCache(result.marketId, result.market, timestamp);
        }
      }

      logDEBUG('_fetchMarketsViaWebSocket', `✓ Fetched ${Object.keys(marketMap).length}/${marketIds.length} markets via WS`);
      return marketMap;
    } catch (error) {
      logWarn('EvmPredictionMarketProvider', '[WS] WebSocket fetch failed', error);

      // If all WS fetches failed, fall back to HTTP (let caller decide)
      throw error;
    }
  }

  async getCreators(page: number): Promise<CreatorDetails[]> {
    // Check cache first
    const now = Date.now();
    const cached = this.creatorCache.get(page);
    if (cached && now - cached.timestamp < this.CREATOR_CACHE_TTL) {
      logDEBUG('getCreators', `CACHED page ${page}`);
      logCacheHit('creator');
      return cached.creators;
    }

    // Check if request is already in flight
    const inFlightRequest = this.inFlightCreatorsRequests.get(page);
    if (inFlightRequest) {
      const currentCount = this.inFlightCreatorsCallerCounts.get(page) || 1;
      this.inFlightCreatorsCallerCounts.set(page, currentCount + 1);
      return inFlightRequest;
    }

    logDEBUG('getCreators', 'page', page);
    logCacheMiss('creator');
    this.inFlightCreatorsCallerCounts.set(page, 1);

    const fetchPromise = this._getCreatorsImpl(page);

    this.inFlightCreatorsRequests.set(page, fetchPromise);
    fetchPromise.finally(() => {
      const callerCount = this.inFlightCreatorsCallerCounts.get(page) || 1;
      if (callerCount > 1) {
        logDEBUG(`[IN-FLIGHT DEDUP]`, `${callerCount} concurrent callers reused request for creators page ${page}`);
      }
      this.inFlightCreatorsRequests.delete(page);
      this.inFlightCreatorsCallerCounts.delete(page);
    });

    return fetchPromise;
  }

  private async _getCreatorsImpl(page: number): Promise<CreatorDetails[]> {
    const now = Date.now();
    const pageSize = CREATORS_PAGE_SIZE;

    const totalCreators = await this.marketContract.creatorsCount();
    const totalPages = Math.ceil((Number(totalCreators) - 1) / pageSize); // -1 to skip creator 0
    const invertedPage = totalPages - page + 1;

    const startId = Math.max(1, (invertedPage - 1) * pageSize + 1); // Start from 1, skip creator 0
    const endId = Math.min(startId + pageSize - 1, Number(totalCreators)); // Cap at actual last creator

    // Collect creator IDs to fetch
    const creatorIds: number[] = [];
    for (let i = startId; i <= endId; i++) {
      creatorIds.push(i);
    }

    if (creatorIds.length === 0) {
      return [];
    }

    // Prepare multicall to batch fetch all creator details AND addresses in one call
    const calls = creatorIds.flatMap((creatorId) => {
      const detailsCallData = this.marketContract.interface.encodeFunctionData('getCreatorDetailsById', [creatorId]);
      const addressCallData = this.marketContract.interface.encodeFunctionData('creatorAddressById', [creatorId]);

      return [
        {
          target: this.MARKET_ADDRESS,
          allowFailure: true,
          callData: detailsCallData,
        },
        {
          target: this.MARKET_ADDRESS,
          allowFailure: true,
          callData: addressCallData,
        },
      ];
    });

    // Execute multicall
    const results = await this.multicall3Contract.aggregate3.staticCall(calls);

    // Parse results first to extract blob IDs
    const creatorData: Array<{
      creatorId: string;
      rawCreator: LibMarket.CreatorStructOutput;
      creatorAddress: string;
      streamUrlBlobId: string;
      descriptionBlobId: string;
    } | null> = [];

    for (let i = 0; i < creatorIds.length; i++) {
      const detailsResult = results[i * 2]!;
      const addressResult = results[i * 2 + 1]!;

      if (!detailsResult.success || !addressResult.success) {
        logWarn('EvmPredictionMarketProvider', `failed to fetch creator with ID ${creatorIds[i]} in multicall`);
        creatorData.push(null);
        continue;
      }

      try {
        const decodedDetails = this.marketContract.interface.decodeFunctionResult('getCreatorDetailsById', detailsResult.returnData);
        const rawCreator = decodedDetails[0] as LibMarket.CreatorStructOutput;

        const decodedAddress = this.marketContract.interface.decodeFunctionResult('creatorAddressById', addressResult.returnData);
        const creatorAddress = decodedAddress[0] as string;

        creatorData.push({
          creatorId: creatorIds[i]!.toString(),
          rawCreator,
          creatorAddress,
          streamUrlBlobId: rawCreator.streamUrl.slice(2),
          descriptionBlobId: rawCreator.description.slice(2),
        });
      } catch (error) {
        logWarn('EvmPredictionMarketProvider', `error parsing creator ${creatorIds[i]}`, error);
        creatorData.push(null);
      }
    }

    // Collect all blob IDs and batch fetch them in one call
    const allBlobIds: string[] = [];
    for (const data of creatorData) {
      if (data) {
        allBlobIds.push(data.streamUrlBlobId, data.descriptionBlobId);
      }
    }

    // Convert to CreatorDetails FIRST without blobs (instant render)
    const filteredCreators = creatorData
      .filter((data): data is NonNullable<typeof data> => data !== null)
      .map((data) => ({
        id: data.rawCreator.creatorId.toString(),
        address: data.creatorAddress,
        nickname: ethers.decodeBytes32String(data.rawCreator.nickname),
        streamUrl: '', // Will be filled async
        description: '', // Will be filled async
      }));

    // Cache the result immediately (without blobs for instant render)
    this.creatorCache.set(page, { creators: filteredCreators, timestamp: now });

    // OPTIMIZATION: Defer blob fetching until browser is idle (non-blocking, off critical path)
    // This ensures blobs don't compete with critical resources (CSS, JS, fonts)
    // Using requestIdleCallback guarantees they're completely off PageSpeed's critical path
    if (allBlobIds.length > 0) {
      const loadBlobs = () => {
        readBlobsBatch(allBlobIds)
          .then((blobResults) => {
            // Update creators in-place with blob data
            for (let i = 0; i < filteredCreators.length; i++) {
              const data = creatorData[i];
              if (data) {
                filteredCreators[i]!.streamUrl = blobResults.get(data.streamUrlBlobId) || '';
                filteredCreators[i]!.description = blobResults.get(data.descriptionBlobId) || 'Failed to load description';
              }
            }
            // Update cache with blob data (triggers Vue reactivity)
            this.creatorCache.set(page, { creators: filteredCreators, timestamp: now });
            logDEBUG('_getCreatorsImpl', `✓ Loaded ${allBlobIds.length} creator blobs (idle, non-blocking)`);
          })
          .catch((blobError) => {
            logWarn('EvmPredictionMarketProvider', 'error batch fetching creator blobs', blobError);
          });
      };

      // Defer to browser idle time (won't block critical path)
      if ('requestIdleCallback' in window) {
        requestIdleCallback(loadBlobs, { timeout: 1500 }); // Max 1.5s wait
      } else {
        // Fallback for older browsers - still defer but with timeout
        setTimeout(loadBlobs, 500);
      }
    }

    return filteredCreators;
  }

  async getPlatformFees(): Promise<bigint> {
    // Platform fees are cached in contractConfig - use that instead of direct contract call
    const config = await this.getContractConfig();
    logDEBUG('getPlatformFees', 'from cached config', config.platformFees);
    return config.platformFees;
  }

  async getCreatorsCount(): Promise<number> {
    // Check cache first
    const cached = this.homePageStatsCache.creatorsCount;
    if (cached) {
      const now = Date.now();
      if (now - cached.timestamp < this.HOMEPAGE_STATS_CACHE_TTL) {
        logDEBUG('getCreatorsCount', 'CACHED', cached.count);
        logCacheHit('homePageStats.creatorsCount');
        return cached.count;
      }
    }

    // Check if request is already in flight
    if (this.inFlightCreatorsCountRequest) {
      this.inFlightCreatorsCountCallerCount++;
      return this.inFlightCreatorsCountRequest;
    }

    // Fetch from contract
    logDEBUG('getCreatorsCount', 'fetching...');
    logCacheMiss('homePageStats.creatorsCount');
    this.inFlightCreatorsCountCallerCount = 1;
    const fetchPromise = (async () => {
      try {
        const count = await this.marketContract.creatorsCount();
        const result = Number(count);
        // Store in cache
        this.homePageStatsCache.creatorsCount = { count: result, timestamp: Date.now() };
        return result;
      } finally {
        const callerCount = this.inFlightCreatorsCountCallerCount;
        if (callerCount > 1) {
          logDEBUG(`[IN-FLIGHT DEDUP]`, `${callerCount} concurrent callers reused request for creators count`);
        }
        this.inFlightCreatorsCountRequest = null;
        this.inFlightCreatorsCountCallerCount = 0;
      }
    })();

    this.inFlightCreatorsCountRequest = fetchPromise;
    return fetchPromise;
  }

  async getMarketsCount(): Promise<number> {
    // Check cache first
    const cached = this.homePageStatsCache.marketsCount;
    if (cached) {
      const now = Date.now();
      if (now - cached.timestamp < this.HOMEPAGE_STATS_CACHE_TTL) {
        logDEBUG('getMarketsCount', 'CACHED', cached.count);
        logCacheHit('homePageStats.marketsCount');
        return cached.count;
      }
    }

    // Check if request is already in flight
    if (this.inFlightMarketsCountRequest) {
      this.inFlightMarketsCountCallerCount++;
      return this.inFlightMarketsCountRequest;
    }

    // Fetch from contract
    logDEBUG('getMarketsCount', 'fetching...');
    logCacheMiss('homePageStats.marketsCount');
    this.inFlightMarketsCountCallerCount = 1;
    const fetchPromise = (async () => {
      try {
        const count = await this.marketContract.marketsCount();
        const result = Number(count);
        // Store in cache
        this.homePageStatsCache.marketsCount = { count: result, timestamp: Date.now() };
        return result;
      } finally {
        const callerCount = this.inFlightMarketsCountCallerCount;
        if (callerCount > 1) {
          logDEBUG(`[IN-FLIGHT DEDUP]`, `${callerCount} concurrent callers reused request for markets count`);
        }
        this.inFlightMarketsCountRequest = null;
        this.inFlightMarketsCountCallerCount = 0;
      }
    })();

    this.inFlightMarketsCountRequest = fetchPromise;
    return fetchPromise;
  }

  async getPredictionsCount(): Promise<number> {
    // Check cache first
    const cached = this.homePageStatsCache.predictionsCount;
    if (cached) {
      const now = Date.now();
      if (now - cached.timestamp < this.HOMEPAGE_STATS_CACHE_TTL) {
        logDEBUG('getPredictionsCount', 'CACHED', cached.count);
        logCacheHit('homePageStats.predictionsCount');
        return cached.count;
      }
    }

    // Check if request is already in flight
    if (this.inFlightPredictionsCountRequest) {
      this.inFlightPredictionsCountCallerCount++;
      return this.inFlightPredictionsCountRequest;
    }

    // Fetch from contract
    logDEBUG('getPredictionsCount', 'fetching...');
    logCacheMiss('homePageStats.predictionsCount');
    this.inFlightPredictionsCountCallerCount = 1;
    const fetchPromise = (async () => {
      try {
        const count = await this.marketContract.predictionsCount();
        const result = Number(count);
        // Store in cache
        this.homePageStatsCache.predictionsCount = { count: result, timestamp: Date.now() };
        return result;
      } finally {
        const callerCount = this.inFlightPredictionsCountCallerCount;
        if (callerCount > 1) {
          logDEBUG(`[IN-FLIGHT DEDUP]`, `${callerCount} concurrent callers reused request for predictions count`);
        }
        this.inFlightPredictionsCountRequest = null;
        this.inFlightPredictionsCountCallerCount = 0;
      }
    })();

    this.inFlightPredictionsCountRequest = fetchPromise;
    return fetchPromise;
  }

  async getCreatorMarketsCount(creatorAddress: string): Promise<number> {
    const checksummed = this.ensureChecksummedAddress(creatorAddress);

    // Reuse getCreatorCounts() which efficiently fetches all counts (all/live/pending/settled)
    // in one multicall with caching. This eliminates expensive individual contract calls.
    const counts = await this.getCreatorCounts(checksummed);
    return counts.all;
  }

  async getCreatorActiveMarketsCount(creatorAddress: string, onlyLive = true): Promise<number> {
    const checksummed = this.ensureChecksummedAddress(creatorAddress);

    // Reuse getCreatorCounts() which efficiently fetches all counts via multicall
    const counts = await this.getCreatorCounts(checksummed);
    return onlyLive ? counts.live : counts.pending;
  }

  async getCreatorSettledMarketsCount(creatorAddress: string): Promise<number> {
    const checksummed = this.ensureChecksummedAddress(creatorAddress);

    // Reuse getCreatorCounts() which efficiently fetches all counts via multicall
    const counts = await this.getCreatorCounts(checksummed);
    return counts.settled;
  }

  async getCreatorCounts(creatorAddress: string): Promise<CreatorCounts> {
    const checksummed = this.ensureChecksummedAddress(creatorAddress);

    // Check cache first
    const cached = this.creatorCountsCache.get(checksummed);
    if (cached && Date.now() - cached.timestamp < this.CREATOR_COUNTS_CACHE_TTL) {
      logDEBUG('getCreatorCounts', 'CACHED', checksummed);
      logCacheHit('creatorCounts');
      return cached.counts;
    }

    logCacheMiss('creatorCounts');

    // Check if request is already in flight
    const inFlightRequest = this.inFlightCreatorCountsRequests.get(checksummed);
    if (inFlightRequest) {
      const currentCount = this.inFlightCreatorCountsCallerCounts.get(checksummed) || 1;
      this.inFlightCreatorCountsCallerCounts.set(checksummed, currentCount + 1);
      return inFlightRequest;
    }

    logDEBUG('getCreatorCounts', checksummed);
    this.inFlightCreatorCountsCallerCounts.set(checksummed, 1);

    const fetchPromise = (async () => {
      try {
        const result = await this._getCreatorCountsImpl(checksummed);
        // Cache the result
        this.creatorCountsCache.set(checksummed, { counts: result, timestamp: Date.now() });
        return result;
      } finally {
        const callerCount = this.inFlightCreatorCountsCallerCounts.get(checksummed) || 1;
        if (callerCount > 1) {
          logDEBUG(`[IN-FLIGHT DEDUP]`, `${callerCount} concurrent callers reused request for creator counts: ${checksummed}`);
        }
        this.inFlightCreatorCountsRequests.delete(checksummed);
        this.inFlightCreatorCountsCallerCounts.delete(checksummed);
      }
    })();

    this.inFlightCreatorCountsRequests.set(checksummed, fetchPromise);
    return fetchPromise;
  }

  private async _getCreatorCountsImpl(checksummed: string): Promise<CreatorCounts> {
    // We do a multicall to fetch all counts in one call for efficiency
    const calls = [
      // All markets count
      {
        target: this.MARKET_ADDRESS,
        allowFailure: true,
        callData: this.marketContract.interface.encodeFunctionData('getCreatorsAllMarketsCount', [checksummed]),
      },
      // Live markets count
      {
        target: this.MARKET_ADDRESS,
        allowFailure: true,
        callData: this.marketContract.interface.encodeFunctionData('getCreatorsActiveMarketsCount', [checksummed, true]),
      },
      // Pending markets count (active but not live)
      {
        target: this.MARKET_ADDRESS,
        allowFailure: true,
        callData: this.marketContract.interface.encodeFunctionData('getCreatorsActiveMarketsCount', [checksummed, false]),
      },
      // Settled markets count
      {
        target: this.MARKET_ADDRESS,
        allowFailure: true,
        callData: this.marketContract.interface.encodeFunctionData('getCreatorsSettledMarketsCount', [checksummed]),
      },
    ];

    const results = await this.multicall3Contract.aggregate3.staticCall(calls);

    const allCount = results[0]?.success
      ? Number(this.marketContract.interface.decodeFunctionResult('getCreatorsAllMarketsCount', results[0].returnData)[0])
      : 0;

    const liveCount = results[1]?.success
      ? Number(this.marketContract.interface.decodeFunctionResult('getCreatorsActiveMarketsCount', results[1].returnData)[0])
      : 0;

    // getCreatorsActiveMarketsCount(creator, false) returns pending markets directly (active but not live)
    const pendingCount = results[2]?.success
      ? Number(this.marketContract.interface.decodeFunctionResult('getCreatorsActiveMarketsCount', results[2].returnData)[0])
      : 0;

    const settledCount = results[3]?.success
      ? Number(this.marketContract.interface.decodeFunctionResult('getCreatorsSettledMarketsCount', results[3].returnData)[0])
      : 0;

    return {
      all: allCount,
      live: liveCount,
      pending: pendingCount,
      settled: settledCount,
    };
  }

  /**
   * Batch fetch active market counts for multiple creators using Multicall3
   */
  async getCreatorActiveMarketsCountBatch(creatorAddresses: string[]): Promise<Record<string, number>> {
    if (creatorAddresses.length === 0) {
      return {};
    }

    // Create a sorted, normalized key for deduplication
    const requestKey = [...creatorAddresses].sort().join(',');

    // Check if this exact request is already in flight
    const inFlightRequest = this.inFlightCreatorActiveMarketsCountRequests.get(requestKey);
    if (inFlightRequest) {
      const currentCount = this.inFlightCreatorActiveMarketsCountCallerCounts.get(requestKey) || 1;
      this.inFlightCreatorActiveMarketsCountCallerCounts.set(requestKey, currentCount + 1);
      return inFlightRequest;
    }

    this.inFlightCreatorActiveMarketsCountCallerCounts.set(requestKey, 1);

    // Create the fetch promise
    const fetchPromise = this._getCreatorActiveMarketsCountBatchImpl(creatorAddresses);

    // Store it to prevent duplicate requests
    this.inFlightCreatorActiveMarketsCountRequests.set(requestKey, fetchPromise);

    // Clean up after completion (success or failure)
    fetchPromise.finally(() => {
      const callerCount = this.inFlightCreatorActiveMarketsCountCallerCounts.get(requestKey) || 1;
      if (callerCount > 1) {
        logDEBUG(
          `[IN-FLIGHT DEDUP]`,
          `${callerCount} concurrent callers reused request for creator active markets count batch: ${creatorAddresses.length} creators`,
        );
      }
      this.inFlightCreatorActiveMarketsCountRequests.delete(requestKey);
      this.inFlightCreatorActiveMarketsCountCallerCounts.delete(requestKey);
    });

    return fetchPromise;
  }

  private async _getCreatorActiveMarketsCountBatchImpl(creatorAddresses: string[]): Promise<Record<string, number>> {
    // Check cache for all addresses
    const now = Date.now();
    const cachedCounts: Record<string, number> = {};
    const addressesToFetch: string[] = [];

    for (const address of creatorAddresses) {
      const checksummed = this.ensureChecksummedAddress(address);
      const cached = this.creatorActiveMarketsCountCache.get(checksummed);

      if (cached && now - cached.timestamp < this.CREATOR_ACTIVE_MARKETS_COUNT_CACHE_TTL) {
        cachedCounts[address] = cached.count;
      } else {
        addressesToFetch.push(address);
      }
    }

    // If all values are cached, return early
    if (addressesToFetch.length === 0) {
      logDEBUG('getCreatorActiveMarketsCountBatch', `CACHED ${creatorAddresses.length} creators`);
      logCacheHit('creatorActiveMarketsCount');
      return cachedCounts;
    }

    // Log cache hits/misses
    if (creatorAddresses.length - addressesToFetch.length > 0) {
      logCacheHit('creatorActiveMarketsCount');
    }
    if (addressesToFetch.length > 0) {
      logCacheMiss('creatorActiveMarketsCount');
    }

    logDEBUG('getCreatorActiveMarketsCountBatch', addressesToFetch.length, 'creators');

    // Checksum all addresses to prevent ENS resolution
    const checksummedAddresses = addressesToFetch.map((addr) => this.ensureChecksummedAddress(addr));

    // Prepare multicall for all active market counts (onlyLive: true to show only live markets)
    const calls = checksummedAddresses.map((address) => {
      const callData = this.marketContract.interface.encodeFunctionData('getCreatorsActiveMarketsCount', [address, true]);
      return {
        target: this.MARKET_ADDRESS,
        allowFailure: true,
        callData,
      };
    });

    // Execute multicall
    const results = await this.multicall3Contract.aggregate3.staticCall(calls);

    // Parse results and update cache
    const fetchedCounts: Record<string, number> = {};
    for (let i = 0; i < results.length; i++) {
      const result = results[i];
      const address = addressesToFetch[i]!;
      const checksummed = checksummedAddresses[i]!;

      if (result?.success) {
        const decodedData = this.marketContract.interface.decodeFunctionResult('getCreatorsActiveMarketsCount', result.returnData);
        const count = Number(decodedData[0]);
        fetchedCounts[address] = count;
        // Update cache
        this.creatorActiveMarketsCountCache.set(checksummed, { count, timestamp: now });
      } else {
        logWarn('EvmPredictionMarketProvider', `failed to fetch active markets count for creator ${address}`);
        fetchedCounts[address] = 0;
        // Cache the failure too (with 0 count)
        this.creatorActiveMarketsCountCache.set(checksummed, { count: 0, timestamp: now });
      }
    }

    // Merge cached and fetched results
    return { ...cachedCounts, ...fetchedCounts };
  }

  /**
   * Batch fetch creator stats for multiple creators using new contract aggregation function
   * @param creatorAddresses Array of creator addresses to fetch stats for
   * @returns Map of creator addresses to their stats
   */
  async getCreatorStatsBatch(creatorAddresses: string[]): Promise<
    Record<
      string,
      {
        totalVolume: number;
        totalEarnings: number;
        totalSettledMarkets: number;
        totalPredictionsCount: number;
        lastActivityTimestamp: number;
      }
    >
  > {
    if (creatorAddresses.length === 0) {
      return {};
    }

    // Create a sorted, normalized key for deduplication
    const requestKey = [...creatorAddresses].sort().join(',');

    // Check if this exact request is already in flight
    const inFlightRequest = this.inFlightCreatorStatsRequests.get(requestKey);
    if (inFlightRequest) {
      const currentCount = this.inFlightCreatorStatsCallerCounts.get(requestKey) || 1;
      this.inFlightCreatorStatsCallerCounts.set(requestKey, currentCount + 1);
      return inFlightRequest;
    }

    this.inFlightCreatorStatsCallerCounts.set(requestKey, 1);

    // Create the fetch promise
    const fetchPromise = this._getCreatorStatsBatchImpl(creatorAddresses);

    // Store it to prevent duplicate requests
    this.inFlightCreatorStatsRequests.set(requestKey, fetchPromise);

    // Clean up after completion (success or failure)
    fetchPromise.finally(() => {
      const callerCount = this.inFlightCreatorStatsCallerCounts.get(requestKey) || 1;
      if (callerCount > 1) {
        logDEBUG(
          `[IN-FLIGHT DEDUP]`,
          `${callerCount} concurrent callers reused request for creator stats batch: ${creatorAddresses.length} creators`,
        );
      }
      this.inFlightCreatorStatsRequests.delete(requestKey);
      this.inFlightCreatorStatsCallerCounts.delete(requestKey);
    });

    return fetchPromise;
  }

  private async _getCreatorStatsBatchImpl(creatorAddresses: string[]): Promise<
    Record<
      string,
      {
        totalVolume: number;
        totalEarnings: number;
        totalSettledMarkets: number;
        totalPredictionsCount: number;
        lastActivityTimestamp: number;
      }
    >
  > {
    // Check cache for all addresses
    const now = Date.now();
    const cachedStats: Record<
      string,
      {
        totalVolume: number;
        totalEarnings: number;
        totalSettledMarkets: number;
        totalPredictionsCount: number;
        lastActivityTimestamp: number;
      }
    > = {};
    const addressesToFetch: string[] = [];

    for (const address of creatorAddresses) {
      const checksummed = this.ensureChecksummedAddress(address);
      const cached = this.creatorStatsCache.get(checksummed);

      if (cached && now - cached.timestamp < this.CREATOR_STATS_CACHE_TTL) {
        cachedStats[address] = cached.stats;
      } else {
        addressesToFetch.push(address);
      }
    }

    // If all values are cached, return early
    if (addressesToFetch.length === 0) {
      logDEBUG('getCreatorStatsBatch', `CACHED ${creatorAddresses.length} creators`);
      logCacheHit('creatorStats');
      return cachedStats;
    }

    // Log cache hits/misses
    if (creatorAddresses.length - addressesToFetch.length > 0) {
      logCacheHit('creatorStats');
    }
    if (addressesToFetch.length > 0) {
      logCacheMiss('creatorStats');
    }

    logDEBUG('getCreatorStatsBatch', addressesToFetch.length, 'creators');

    // Checksum all addresses to prevent ENS resolution
    const checksummedAddresses = addressesToFetch.map((addr) => this.ensureChecksummedAddress(addr));

    // Build multicall for new getCreatorSettledStats function
    const calls = checksummedAddresses.map((address) => {
      const callData = this.marketContract.interface.encodeFunctionData('getCreatorSettledStats', [address]);
      return {
        target: this.MARKET_ADDRESS,
        allowFailure: true,
        callData,
      };
    });

    // Execute multicall
    const results = await this.multicall3Contract.aggregate3.staticCall(calls);

    // Get token decimals for volume conversion
    const decimals = await this.getTokenDecimals();

    // Build result map and update cache
    const fetchedStats: Record<
      string,
      {
        totalVolume: number;
        totalEarnings: number;
        totalSettledMarkets: number;
        totalPredictionsCount: number;
        lastActivityTimestamp: number;
      }
    > = {};

    for (let i = 0; i < results.length; i++) {
      const callResult = results[i];
      const address = addressesToFetch[i]!;
      const checksummed = checksummedAddresses[i]!;

      if (callResult?.success) {
        const decodedData = this.marketContract.interface.decodeFunctionResult('getCreatorSettledStats', callResult.returnData);

        // Contract returns: (settledMarketsCount, totalVolume, totalCreatorFees, totalPredictions, lastActivityTimestamp)
        const settledMarketsCount = Number(decodedData[0]);
        const totalVolumeWei = decodedData[1];
        const totalCreatorFeesWei = decodedData[2];
        const totalPredictions = Number(decodedData[3]);
        const lastActivityTimestamp = Number(decodedData[4]);

        // Convert volume and fees from wei to normal units
        const totalVolume = Number(formatUnits(totalVolumeWei, decimals));
        const totalEarnings = Number(formatUnits(totalCreatorFeesWei, decimals));

        const stats = {
          totalVolume,
          totalEarnings,
          totalSettledMarkets: settledMarketsCount,
          totalPredictionsCount: totalPredictions,
          lastActivityTimestamp,
        };

        fetchedStats[address] = stats;
        // Update cache
        this.creatorStatsCache.set(checksummed, { stats, timestamp: now });
      } else {
        logWarn('EvmPredictionMarketProvider', `failed to fetch stats for creator ${address}`);
        const fallbackStats = {
          totalVolume: 0,
          totalEarnings: 0,
          totalSettledMarkets: 0,
          totalPredictionsCount: 0,
          lastActivityTimestamp: 0,
        };
        fetchedStats[address] = fallbackStats;
        // Cache the failure too
        this.creatorStatsCache.set(checksummed, { stats: fallbackStats, timestamp: now });
      }
    }

    // Merge cached and fetched results
    return { ...cachedStats, ...fetchedStats };
  }

  async getMarketsByCreatorAddress(creatorAddress: string, page: number = 1) {
    const checksummed = this.ensureChecksummedAddress(creatorAddress);
    const requestKey = `${checksummed}:${page}`;

    // Check if request is already in flight
    const inFlightRequest = this.inFlightMarketsByCreatorRequests.get(requestKey);
    if (inFlightRequest) {
      const currentCount = this.inFlightMarketsByCreatorCallerCounts.get(requestKey) || 1;
      this.inFlightMarketsByCreatorCallerCounts.set(requestKey, currentCount + 1);
      return inFlightRequest;
    }

    logDEBUG('getMarketsByCreatorAddress', checksummed, `page ${page}`);
    this.inFlightMarketsByCreatorCallerCounts.set(requestKey, 1);

    const fetchPromise = (async () => {
      try {
        return await this._getMarketsByCreatorAddressImpl(checksummed, page);
      } finally {
        const callerCount = this.inFlightMarketsByCreatorCallerCounts.get(requestKey) || 1;
        if (callerCount > 1) {
          logDEBUG(
            `[IN-FLIGHT DEDUP]`,
            `${callerCount} concurrent callers reused request for markets by creator: ${checksummed} page ${page}`,
          );
        }
        this.inFlightMarketsByCreatorRequests.delete(requestKey);
        this.inFlightMarketsByCreatorCallerCounts.delete(requestKey);
      }
    })();

    this.inFlightMarketsByCreatorRequests.set(requestKey, fetchPromise);
    return fetchPromise;
  }

  private async _getMarketsByCreatorAddressImpl(checksummed: string, page: number) {
    const pageSize = CREATOR_MARKETS_PAGE_SIZE;

    // Use getCreatorCounts() which efficiently fetches all counts via multicall with caching
    const counts = await this.getCreatorCounts(checksummed);
    const totalCount = counts.all;

    // Calculate how many markets we've already shown
    const alreadyShown = (page - 1) * pageSize;

    // If we're past all markets, return empty
    if (alreadyShown >= totalCount) {
      return [];
    }

    // Calculate the range we want (in contract's 0-indexed order)
    // Contract stores markets oldest → newest (0 to totalCount-1)
    // UI wants newest → oldest, so we work backwards from the end
    const endIndex = totalCount - 1 - alreadyShown; // Most recent index we want
    const startIndex = Math.max(0, endIndex - pageSize + 1); // Oldest index we want for this page
    const itemsToFetch = endIndex - startIndex + 1; // How many items in this range

    // Determine which contract page(s) contain our range
    // Contract page N contains indices [N * pageSize, (N+1) * pageSize - 1]
    const startContractPage = Math.floor(startIndex / pageSize);
    const endContractPage = Math.floor(endIndex / pageSize);

    const allMarketsData: LibMarket.MarketStructOutput[] = [];

    // Fetch all necessary contract pages in parallel
    // IMPORTANT: Pass ZERO_ADDRESS to get ALL predictions for correct settlement/creator fees calculation
    // User-specific predictions are fetched separately via getMarketById() when needed
    const pagePromises = [];
    for (let contractPage = startContractPage; contractPage <= endContractPage; contractPage++) {
      pagePromises.push(this.marketContract.getCreatorAllMarkets(checksummed, toBigInt(pageSize), toBigInt(contractPage), ZERO_ADDRESS));
    }

    const pagesData = await Promise.all(pagePromises);
    for (const pageData of pagesData) {
      allMarketsData.push(...pageData);
    }

    // Extract the exact slice we need from the fetched pages
    // Offset within the first contract page we fetched
    const offsetInFirstPage = startIndex - startContractPage * pageSize;
    const marketsData = allMarketsData.slice(offsetInFirstPage, offsetInFirstPage + itemsToFetch);

    // Extract market IDs and check cache first (optimization for navigating between pages)
    const marketIds = marketsData.map((m) => m.id.toString());
    const cachedMarkets = await this.getMarketsByIds(marketIds);

    // Build result array in correct order using cached markets
    const markets: Market[] = [];
    for (const marketId of marketIds) {
      const cached = cachedMarkets[marketId];
      if (cached) {
        markets.push(cached);
      }
    }

    // Reverse to show newest first
    markets.reverse();

    return markets;
  }

  /**
   * Create a new prediction market.
   *
   * Features:
   * - Automatic blob storage for rules text
   * - Parses CreatedMarket event to return new market ID
   * - Validates probability basis points (must sum to 10000)
   * - Encodes strings to bytes32 for gas efficiency
   *
   * Requirements:
   * - Caller must be a registered creator
   * - Fee must be between minFees and maxFees (contract config)
   * - End time must be <= maxTime from now
   * - Probabilities must sum to 100% (10000 bps)
   * - Title and outcome names must fit in bytes32 (max 31 chars)
   *
   * Blockchain Events:
   * - Emits `CreatedMarket(creatorAddress, marketId, ...)`
   * - Triggers automatic cache invalidation
   * - Notifies subscribed listeners
   * - Shows success notification to creator
   *
   * @param title - Market title (max 31 chars, ASCII only)
   * @param marketType - 0=Binary, 1=Categorical, 2=Scalar
   * @param outcomes - Array of {name, probability} (probabilities in decimals, e.g., 0.5 = 50%)
   * @param endUnixTime - Market end time (Unix timestamp in seconds)
   * @param rules - Market rules/description (stored in blob, any length)
   * @param feeBps - Creator fee in basis points (0-10000, e.g., 100 = 1%)
   * @param minPredictionAmount - Minimum bet in wei (bigint)
   * @returns Promise<MarketId> - Newly created market ID
   * @throws {Error} If not creator, invalid params, or transaction fails
   *
   * @example
   * ```typescript
   * const marketId = await provider.createMarket(
   *   'Will it rain tomorrow?',
   *   0, // Binary
   *   [{name: 'Yes', probability: 0.5}, {name: 'No', probability: 0.5}],
   *   Math.floor(Date.now()/1000) + 86400, // 24h from now
   *   'Market settles based on weather.com data',
   *   100n, // 1% fee
   *   parseUnits('1', 18) // Min 1 token
   * );
   * ```
   */
  async createMarket(
    title: string,
    marketType: MarketType,
    outcomes: OutcomeInput[],
    endUnixTime: number,
    rules: string,
    feeBps: number,
    minPredictionAmount: bigint,
  ): Promise<MarketId> {
    const marketWithSigner = await this.getMarketWithSigner();

    const titleBytes32 = ethers.encodeBytes32String(title);

    // Convert probabilities to basis points with rounding to avoid floating point precision issues
    const probBpsPerOutcome = outcomes.map((outcome) => Math.round(Number(outcome.probability) * 100));
    const outcomeDetails = outcomes.map((outcome) => ({
      title: ethers.encodeBytes32String(outcome.name),
    }));

    const affiliateId = 0;

    const rulesBlobResponse = await createBlob(rules);
    const rulesBytes32 = '0x' + rulesBlobResponse.id;

    const tx = await marketWithSigner.createMarket(
      titleBytes32,
      marketType,
      rulesBytes32,
      feeBps,
      probBpsPerOutcome,
      endUnixTime,
      minPredictionAmount,
      outcomeDetails,
      affiliateId,
    );

    const receipt = await tx.wait();
    if (!receipt) throw new Error('Transaction was not mined');

    for (const log of receipt.logs) {
      if (log.address.toLowerCase() !== this.MARKET_ADDRESS.toLowerCase()) continue;

      const parsed = this.marketContract.interface.parseLog(log);
      if (parsed?.name === BlockchainEvent.CREATED_MARKET) {
        const marketId = parsed.args[1] as bigint;
        return Number(marketId) as MarketId;
      }
    }

    throw new Error('CreatedMarket event not found in receipt logs');
  }

  async registerUserAsCreator(nickname: string, streamUrl: string, description: string): Promise<boolean> {
    // Parallelize blob creation
    const [streamUrlHashResponse, descriptionHashResponse] = await Promise.all([createBlob(streamUrl), createBlob(description)]);

    const marketWithSigner = await this.getMarketWithSigner();

    const streamUrlBytes32 = '0x' + streamUrlHashResponse.id;
    const descriptionBytes32 = '0x' + descriptionHashResponse.id;

    // v6 bytes32 helpers
    const nicknameBytes32 = ethers.encodeBytes32String(nickname);
    const metadataUriBytes32 = ethers.encodeBytes32String('NOT_USED'); // Empty for now

    const tx = await marketWithSigner.registerCreator(nicknameBytes32, streamUrlBytes32, descriptionBytes32, metadataUriBytes32);

    const receipt = await tx.wait();
    return receipt !== null;
  }

  async updateUserAsCreator(nickname: string, streamUrl: string, description: string): Promise<boolean> {
    const marketWithSigner = await this.getMarketWithSigner();

    // Parallelize blob creates
    const [streamUrlHashResponse, descriptionHashResponse] = await Promise.all([createBlob(streamUrl), createBlob(description)]);

    const streamUrlBytes32 = '0x' + streamUrlHashResponse.id;
    const descriptionBytes32 = '0x' + descriptionHashResponse.id;

    // v6 bytes32 helpers
    const nicknameBytes32 = ethers.encodeBytes32String(nickname);
    const metadataUriBytes32 = ethers.encodeBytes32String('NOT_USED'); // Empty for now

    const tx = await marketWithSigner.updateCreator(nicknameBytes32, streamUrlBytes32, descriptionBytes32, metadataUriBytes32);

    const receipt = await tx.wait();
    return receipt !== null;
  }

  /**
   * Fetch creator details by address with caching and in-flight deduplication.
   *
   * Features:
   * - 5-minute TTL cache
   * - In-flight request deduplication
   * - Blob fetching for stream URL and description
   * - Checksummed addresses (prevents ENS resolution attempts)
   *
   * Supports smart provider selection:
   * - 'http' (default): HTTP provider
   * - 'ws': WebSocket provider (instant for event-driven queries)
   * - 'auto': Try WebSocket first, fallback to HTTP
   *
   * @param creatorAddress - Creator's Ethereum address (must be valid 0x... address)
   * @param preferProvider - Provider selection mode ('http' | 'ws' | 'auto')
   * @returns Promise resolving to CreatorDetails object
   * @throws {Error} If address format is invalid
   * @throws {UserNotCreatorError} If address is not a registered creator
   *
   * @example
   * ```typescript
   * // Standard fetch
   * const creator = await provider.getCreatorDetails('0x1234...');
   *
   * // Event-driven fetch (instant)
   * const creator = await provider.getCreatorDetails('0x1234...', 'auto');
   * console.log(`Creator: ${creator.nickname}`);
   * ```
   */
  async getCreatorDetails(creatorAddress: string, preferProvider: 'http' | 'ws' | 'auto' = 'http'): Promise<CreatorDetails> {
    // Validate and checksum the address (pure function, no network calls)
    // Checksummed addresses prevent ethers from trying ENS resolution
    if (!/^0x[0-9a-fA-F]{40}$/.test(creatorAddress)) {
      throw new Error(`Invalid creator address format: ${creatorAddress}`);
    }
    // Use checksummed address to avoid ENS resolution attempts by ethers
    const checksummedAddress = getAddress(creatorAddress);

    // Check cache first
    const now = Date.now();
    const cached = this.creatorDetailsCache.get(checksummedAddress);
    if (cached && now - cached.timestamp < this.CREATOR_DETAILS_CACHE_TTL) {
      logDEBUG('getCreatorDetails', 'CACHED', checksummedAddress);
      logCacheHit('creatorDetails');
      return cached.details;
    }

    logCacheMiss('creatorDetails');

    // Check if request is already in flight (ignore provider for max dedup)
    const inFlightRequest = this.inFlightCreatorDetailsRequests.get(checksummedAddress);
    if (inFlightRequest) {
      const currentCount = this.inFlightCreatorDetailsCallerCounts.get(checksummedAddress) || 1;
      this.inFlightCreatorDetailsCallerCounts.set(checksummedAddress, currentCount + 1);
      return inFlightRequest;
    }

    logDEBUG('getCreatorDetails', checksummedAddress, preferProvider !== 'http' ? `(prefer: ${preferProvider})` : '');
    this.inFlightCreatorDetailsCallerCounts.set(checksummedAddress, 1);

    const fetchPromise = (async () => {
      try {
        // Determine which provider to use
        let useWs = false;
        if (preferProvider === 'ws' && this.wsMarketContract) {
          useWs = true;
        } else if (preferProvider === 'auto' && this.wsMarketContract) {
          useWs = true; // Try WS first in auto mode
        }

        let details;
        try {
          if (useWs) {
            details = await this.wsMarketContract!.creators(checksummedAddress);
          } else {
            details = await this.marketContract.creators(checksummedAddress);
          }
        } catch (error) {
          // If WS failed in auto mode, fall back to HTTP
          if (preferProvider === 'auto' && useWs) {
            logWarn('EvmPredictionMarketProvider', 'WS creator fetch failed in auto mode, falling back to HTTP', error);
            details = await this.marketContract.creators(checksummedAddress);
          } else {
            throw error;
          }
        }

        const creatorId = details.creatorId.toString();

        if (creatorId === '0') {
          throw new UserNotCreatorError(`User with address ${checksummedAddress} is not a registered creator.`);
        }

        let streamUrl = '';
        let description;

        // Parallelize blob fetches
        try {
          const [streamUrlBlobResponse, descriptionBlobResponse] = await Promise.all([
            readBlob(details.streamUrl.slice(2)),
            readBlob(details.description.slice(2)),
          ]);

          streamUrl = streamUrlBlobResponse || '';
          description = descriptionBlobResponse || '';
        } catch (error) {
          logWarn('EvmPredictionMarketProvider', `error fetching blobs for creator ${checksummedAddress}`, error);
          description = '';
        }

        const creatorDetails = {
          id: details.creatorId.toString(),
          address: checksummedAddress.toLowerCase(),
          nickname: ethers.decodeBytes32String(details.nickname),
          streamUrl,
          description,
        };

        // Update cache
        this.creatorDetailsCache.set(checksummedAddress, {
          details: creatorDetails,
          timestamp: Date.now(),
        });

        return creatorDetails;
      } catch (error) {
        // Re-throw all errors (including UserNotCreatorError and validation errors)
        throw error;
      } finally {
        const callerCount = this.inFlightCreatorDetailsCallerCounts.get(checksummedAddress) || 1;
        if (callerCount > 1) {
          logDEBUG(`[IN-FLIGHT DEDUP]`, `${callerCount} concurrent callers reused request for creator details: ${checksummedAddress}`);
        }
        this.inFlightCreatorDetailsRequests.delete(checksummedAddress);
        this.inFlightCreatorDetailsCallerCounts.delete(checksummedAddress);
      }
    })();

    this.inFlightCreatorDetailsRequests.set(checksummedAddress, fetchPromise);
    return fetchPromise;
  }

  // We use the creatorAddressById mapping from the smart contract
  async getCreatorAddressFromCreatorId(creatorId: string): Promise<string> {
    const id = Number(creatorId);
    if (!Number.isInteger(id) || id < 0 || id > 0xffffffff) {
      throw new Error('creatorId must be a uint32');
    }

    // Check cache first (permanent cache since mapping never changes)
    const cached = this.creatorAddressByIdCache.get(creatorId);
    if (cached) {
      logDEBUG('getCreatorAddressFromCreatorId', 'CACHED', creatorId);
      logCacheHit('creatorAddressById');
      return cached;
    }

    logCacheMiss('creatorAddressById');

    // Check if request is already in flight
    const inFlightRequest = this.inFlightCreatorAddressRequests.get(creatorId);
    if (inFlightRequest) {
      const currentCount = this.inFlightCreatorAddressCallerCounts.get(creatorId) || 1;
      this.inFlightCreatorAddressCallerCounts.set(creatorId, currentCount + 1);
      return inFlightRequest;
    }

    logDEBUG('getCreatorAddressFromCreatorId', creatorId);
    this.inFlightCreatorAddressCallerCounts.set(creatorId, 1);

    const fetchPromise = (async () => {
      try {
        const creatorAddress = await this.marketContract.creatorAddressById(id);
        if (creatorAddress === ZERO_ADDRESS) {
          throw new Error(`No creator found with creatorId ${creatorId}`);
        }

        // Update cache (permanent - never expires)
        this.creatorAddressByIdCache.set(creatorId, creatorAddress);

        return creatorAddress;
      } finally {
        const callerCount = this.inFlightCreatorAddressCallerCounts.get(creatorId) || 1;
        if (callerCount > 1) {
          logDEBUG(`[IN-FLIGHT DEDUP]`, `${callerCount} concurrent callers reused request for creator address from ID: ${creatorId}`);
        }
        this.inFlightCreatorAddressRequests.delete(creatorId);
        this.inFlightCreatorAddressCallerCounts.delete(creatorId);
      }
    })();

    this.inFlightCreatorAddressRequests.set(creatorId, fetchPromise);
    return fetchPromise;
  }

  /**
   * Subscribe to blockchain market events with dual-provider architecture.
   *
   * **Event Types:**
   * - `CreatedMarket`: New market created (global, all users)
   * - `SettledMarket`: Market settled with winning outcome (global, all users)
   * - `PlacedPrediction`: New prediction placed (user-specific notifications)
   * - `CancelledMarket`: Market cancelled/voided (global, all users)
   * - `CreatedCreator`: New creator registered (global, homepage updates)
   * - `UpdatedCreator`: Creator profile updated (global, homepage updates)
   *
   * **Provider Strategy:**
   * - HTTP provider: 1s polling (reliable fallback)
   * - WebSocket provider: Real-time events ~100ms latency
   * - Event deduplication: Prevents duplicate processing from both providers
   *
   * **Listener Lifecycle:**
   * - Registered on both HTTP and WebSocket providers (if available)
   * - Automatically re-subscribed after WebSocket reconnection
   * - Call `unsubscribeFromMarketUpdates()` to clean up
   *
   * **Cache Invalidation:**
   * - Automatically clears relevant caches on events
   * - Triggers balance refresh callbacks for user's own transactions
   * - Emits success notifications for current user
   *
   * @param listener - Callback function receiving Market objects
   * @returns Promise<Listener> - Ethers.js listener (not used directly, cleanup via unsubscribe)
   *
   * @example
   * ```typescript
   **/
  async subscribeToMarketUpdates(listener: MarketListener): Promise<Listener> {
    const createdMarketEvent = this.marketContract.filters.CreatedMarket;
    const settledMarketEvent = this.marketContract.filters.SettledMarket;
    const placedPredictionEvent = this.marketContract.filters.PlacedPrediction;
    const cancelledMarketEvent = this.marketContract.filters.CancelledMarket;
    const createdCreatorEvent = this.marketContract.filters.CreatedCreator;
    const updatedCreatorEvent = this.marketContract.filters.UpdatedCreator;
    // Approval: filter to the current user's address at the node level (owner is indexed).
    // If no wallet is connected yet, or the active account is a play account (non-EVM address
    // like "0xplay_account"), skip the subscription — ethers would try to resolve the value
    // as an ENS name and throw.
    const rawUserAddr = getCurrentUserAddress();
    const currentUserAddrForFilter = rawUserAddr && isAddress(rawUserAddr) ? rawUserAddr : null;
    const tokenApprovedEvent =
      currentUserAddrForFilter && this.tokenContract ? this.tokenContract.filters.Approval!(getAddress(currentUserAddrForFilter)) : null;

    // Transfer: disabled on mainnet — widely-used token and its Transfer event
    // generates enormous WS traffic (hundreds/min) from unrelated users on BSC mainnet.
    // On mainnet we rely on the transaction-confirmation flow for balance refreshes instead.
    // TODO: To enable properly on mainnet:
    //   1. Subscribe with TWO user-specific filters:
    //        filters.Transfer(userAddr, null)  — outgoing
    //        filters.Transfer(null, userAddr)  — incoming
    //   2. Re-subscribe (re-call subscribeToMarketUpdates or a dedicated helper) when the
    //      wallet connects/changes, so the user address is known at subscription time.
    //   3. Store the two filter references as class fields so unsubscribe can pass the
    //      exact same filters to contract.off().
    const tokenTransferredEvent = IS_MAINNET ? null : this.tokenContract?.filters.Transfer;

    // Store listeners for cleanup in unsubscribeFromMarketUpdates
    this.createdMarketListener = async (...args) => {
      // Deduplicate events (prevent duplicate cache clears from multiple subscriptions)
      if (!this.deduplicateEvent(BlockchainEvent.CREATED_MARKET, args)) {
        return;
      }

      // Track event for connection quality monitoring
      this.wsLastEventTimestamp = Date.now();

      const creatorWallet = args[0] as string; // Event: CreatedMarket(wallet, id, ...)
      const newMarketId = args[1] as bigint;

      // Validate event data (skip invalid markets)
      if (!this.isValidEventData(newMarketId, creatorWallet, 'CreatedMarket')) {
        return;
      }

      // Check if event is from current user - trigger user-specific callbacks
      const currentUserAddr = getCurrentUserAddress();
      if (currentUserAddr && creatorWallet.toLowerCase() === currentUserAddr.toLowerCase()) {
        triggerMarketCreatedCallbacks(); // Success popup
        triggerBalanceRefreshWithDelay(); // Balance update
      }

      // Clear cache BEFORE fetch to force fresh data from RPC
      this.clearMarketCache([newMarketId.toString()], 'CreatedMarket event');

      // OPTIMIZATION: Fetch market and notify listener immediately, clear caches in background
      // This makes the UI update appear faster by not blocking on cache operations
      // RACE CONDITION FIX: Retry fetching if we get invalid data (RPC state not updated yet)
      const fetchMarketWithRetry = async (marketId: string, retries = 5, delay = 300): Promise<void> => {
        for (let attempt = 1; attempt <= retries; attempt++) {
          const market = await this.getMarketById(marketId, 'auto');

          // Validate fetched market data (defensive check for invalid blockchain state)
          // Sometimes events fire but market data is not yet fully written to RPC node
          if (!isValidMarket(market)) {
            if (attempt < retries) {
              logDEBUG('[RETRY]', `Market ${marketId} not ready yet (attempt ${attempt}/${retries}), retrying in ${delay}ms`, {
                marketId: market.id,
                creatorId: market.creatorId,
              });
              // Clear cache before retry to force fresh fetch
              this.clearMarketCache([marketId], 'retry fetch');
              await new Promise((resolve) => setTimeout(resolve, delay));
              delay = Math.min(delay * 2, 1000); // Exponential backoff, capped at 1s
              continue;
            } else {
              logDEBUG('[ERROR]', 'Market data still invalid after retries', {
                marketId: market.id,
                creatorId: market.creatorId,
                attempts: retries,
              });
              return; // Give up after max retries
            }
          }

          // Success! Market data is valid
          // Clear related caches BEFORE emitting event (so HomePage can fetch fresh data)
          // Note: Market cache for this ID was already cleared before fetch (line 2614)
          // Note: market.creatorId is already the creator address (not a numeric ID)
          this.clearCreatorCountsCache(market.creatorId); // All counts changed (live+1, all+1)
          this.clearCreatorActiveMarketsCountCache(market.creatorId); // Live badge count changed (live+1)
          this.clearCreatorCache(); // Homepage creator list
          this.clearHomePageStatsCache(); // Homepage stats (3 counts)

          // Force immediate cache flush before emitting event
          // This ensures HomePage gets fresh data when it refetches
          this.flushCacheClearBatch();

          // Now emit the event
          listener(market);
          return; // Exit retry loop on success
        }
      };

      // Start fetching with retry logic (non-blocking)
      fetchMarketWithRetry(newMarketId.toString()).catch((err) => {
        if (this.shouldLogError(err)) {
          logDEBUG('[ERROR]', 'Error processing CreatedMarket event', err);
        }
      });
    };

    this.settledMarketListener = async (...args) => {
      // Deduplicate events (prevent duplicate cache clears from multiple subscriptions)
      if (!this.deduplicateEvent(BlockchainEvent.SETTLED_MARKET, args)) {
        return;
      }

      // Track event for connection quality monitoring
      this.wsLastEventTimestamp = Date.now();

      const settlerWallet = args[0] as string; // Event: SettledMarket(wallet, id, ...)
      const settledMarketId = args[1] as bigint;

      // Validate event data (skip invalid markets)
      if (!this.isValidEventData(settledMarketId, settlerWallet, 'SettledMarket')) {
        return;
      }

      // Trigger callbacks and balance refresh for all users
      // Balance refresh is needed for anyone with predictions on this market (winners/losers)
      // Not just for the settler - market settlement affects all participants
      triggerMarketSettledCallbacks(); // Success popup for anyone watching
      triggerBalanceRefreshWithDelay(); // Balance update for all participants

      // Clear cache BEFORE fetch to force fresh data from RPC
      this.clearMarketCache([settledMarketId.toString()], 'SettledMarket event');

      // OPTIMIZATION: Fetch market and notify listener immediately, clear caches in background
      // This makes the UI update appear faster by not blocking on cache operations
      // RACE CONDITION FIX: Retry fetching if we get invalid data (RPC state not updated yet)
      const fetchMarketWithRetry = async (marketId: string, retries = 5, delay = 300): Promise<void> => {
        for (let attempt = 1; attempt <= retries; attempt++) {
          const market = await this.getMarketById(marketId, 'auto');

          // Validate fetched market data (defensive check for invalid blockchain state)
          // Sometimes events fire but market data is not yet fully written to RPC node
          if (!isValidMarket(market)) {
            if (attempt < retries) {
              logDEBUG('[RETRY]', `Market ${marketId} not ready (SettledMarket, attempt ${attempt}/${retries}), retrying in ${delay}ms`, {
                marketId: market.id,
                creatorId: market.creatorId,
              });
              // Clear cache before retry to force fresh fetch
              this.clearMarketCache([marketId], 'retry fetch');
              await new Promise((resolve) => setTimeout(resolve, delay));
              delay = Math.min(delay * 2, 1000); // Exponential backoff, capped at 1s
              continue;
            } else {
              logDEBUG('[ERROR]', 'Market data still invalid after retries (SettledMarket)', {
                marketId: market.id,
                creatorId: market.creatorId,
                attempts: retries,
              });
              return; // Give up after max retries
            }
          }

          // Success! Market data is valid
          listener(market);

          // Clear related caches in background after UI is updated (non-blocking)
          // Note: Market cache for this ID was already cleared before fetch
          // Cache clearing is cheap but we don't need to block the UI on it
          // Note: market.creatorId is already the creator address (not a numeric ID)
          this.clearCreatorCountsCache(market.creatorId); // live/pending decreased, settled increased
          this.clearCreatorActiveMarketsCountCache(market.creatorId); // Live badge count changed (live-1)
          this.clearCreatorStatsCache(market.creatorId); // volume, earnings, settled markets count changed
          this.clearCreatorCache(); // Homepage creator list
          return; // Exit retry loop on success
        }
      };

      // Start fetching with retry logic (non-blocking)
      fetchMarketWithRetry(settledMarketId.toString()).catch((err) => {
        if (this.shouldLogError(err)) {
          logDEBUG('[ERROR]', 'Error processing SettledMarket event', err);
        }
      });
    };

    this.placedPredictionListener = async (...args) => {
      // Deduplicate events (prevent duplicate cache clears from multiple subscriptions)
      if (!this.deduplicateEvent(BlockchainEvent.PLACED_PREDICTION, args)) {
        return;
      }

      // Track event for connection quality monitoring
      this.wsLastEventTimestamp = Date.now();

      const predictorWallet = args[0] as string; // Event: PlacedPrediction(wallet, marketId, ...)
      const affectedMarketId = args[1] as bigint;

      // Validate event data (skip invalid markets)
      if (!this.isValidEventData(affectedMarketId, predictorWallet, 'PlacedPrediction')) {
        return;
      }

      // Check if event is from current user - trigger user-specific callbacks
      const currentUserAddr = getCurrentUserAddress();
      if (currentUserAddr && predictorWallet.toLowerCase() === currentUserAddr.toLowerCase()) {
        triggerPredictionPlacedCallbacks(); // Success popup
        triggerBalanceRefreshWithDelay(); // Balance update
      }

      // Clear cache BEFORE fetch to force fresh data from RPC
      // The in-flight request deduplication prevents duplicate RPC calls
      this.clearMarketCache([affectedMarketId.toString()], 'PlacedPrediction event');

      // OPTIMIZATION: Fetch market and notify listener immediately, handle stats/events in background
      // This makes the UI update appear faster by not blocking on creator details or cache operations
      // RACE CONDITION FIX: Retry fetching if we get invalid data (RPC state not updated yet)
      const fetchMarketWithRetry = async (marketId: string, retries = 5, delay = 300): Promise<void> => {
        for (let attempt = 1; attempt <= retries; attempt++) {
          const market = await this.getMarketById(marketId, 'auto');

          // Validate fetched market data (defensive check for invalid blockchain state)
          // Sometimes events fire but market data is not yet fully written to RPC node
          if (!isValidMarket(market)) {
            if (attempt < retries) {
              logDEBUG(
                '[RETRY]',
                `Market ${marketId} not ready (PlacedPrediction, attempt ${attempt}/${retries}), retrying in ${delay}ms`,
                {
                  marketId: market.id,
                  creatorId: market.creatorId,
                },
              );
              // Clear cache before retry to force fresh fetch
              this.clearMarketCache([marketId], 'retry fetch');
              await new Promise((resolve) => setTimeout(resolve, delay));
              delay = Math.min(delay * 2, 1000); // Exponential backoff, capped at 1s
              continue;
            } else {
              logDEBUG('[ERROR]', 'Market data still invalid after retries (PlacedPrediction)', {
                marketId: market.id,
                creatorId: market.creatorId,
                attempts: retries,
              });
              return; // Give up after max retries
            }
          }

          // Success! Market data is valid
          // Call listener as soon as market data is ready (don't wait for cache clears or events)
          listener(market);

          // Fetch creator details and emit event in background (non-blocking for UI)
          // This allows homepage badges to update reactively
          let creator: CreatorDetails;
          try {
            creator = await this.getCreatorDetails(market.creatorId, 'auto');
          } catch (error) {
            logDEBUG('[ERROR]', 'Failed to fetch creator for event:', error);
            return;
          }

          // Clear caches in background after UI is updated (non-blocking)
          this.clearCreatorStatsCache(creator.address); // Creator's total predictions/volume changed
          this.clearHomePageStatsCache(); // Homepage stats (predictions count changed)

          // Force immediate cache flush before emitting event
          // This ensures HomePage gets fresh data when it refetches
          logDEBUG('[CACHE FLUSH]', 'Flushing pending clears before event emission');
          this.flushCacheClearBatch();
          logDEBUG('[CACHE FLUSH]', 'Flush completed, emitting event');

          // Emit event to notify UI components (homepage creator stats badges)
          const events = useEvents();
          events.emit(EVENTS.PREDICTION_PLACED, {
            userId: predictorWallet.toLowerCase(),
            marketId: market.id,
            marketTitle: market.title,
            outcomeId: args[2]?.toString() || '0',
            outcomeName: market.outcomes[Number(args[2])]?.name || 'Unknown',
            amount: args[3] as bigint,
            creatorId: creator.id,
          });

          return; // Exit retry loop on success
        }
      };

      // Start fetching with retry logic (non-blocking)
      fetchMarketWithRetry(affectedMarketId.toString()).catch((err) => {
        if (this.shouldLogError(err)) {
          logDEBUG('[ERROR]', 'Error processing PlacedPrediction event', err);
        }
      });
    };

    this.cancelledMarketListener = async (...args) => {
      // Deduplicate events (prevent duplicate cache clears from multiple subscriptions)
      if (!this.deduplicateEvent(BlockchainEvent.CANCELLED_MARKET, args)) {
        return;
      }

      // Track event for connection quality monitoring
      this.wsLastEventTimestamp = Date.now();

      const cancellerWallet = args[0] as string; // Event: CancelledMarket(wallet, id, ...)
      const cancelledMarketId = args[1] as bigint;

      // Validate event data (skip invalid markets)
      if (!this.isValidEventData(cancelledMarketId, cancellerWallet, 'CancelledMarket')) {
        return;
      }

      // Check if event is from current user - trigger user-specific callbacks
      const currentUserAddr = getCurrentUserAddress();
      if (currentUserAddr && cancellerWallet.toLowerCase() === currentUserAddr.toLowerCase()) {
        triggerMarketCancelledCallbacks(); // Success popup
        triggerBalanceRefreshWithDelay(); // Balance update
      }

      // Clear cache BEFORE fetch to force fresh data from RPC
      this.clearMarketCache([cancelledMarketId.toString()], 'CancelledMarket event');

      // OPTIMIZATION: Fetch market and notify listener immediately, clear caches in background
      // This makes the UI update appear faster by not blocking on cache operations
      // RACE CONDITION FIX: Retry fetching if we get invalid data (RPC state not updated yet)
      const fetchMarketWithRetry = async (marketId: string, retries = 5, delay = 300): Promise<void> => {
        for (let attempt = 1; attempt <= retries; attempt++) {
          const market = await this.getMarketById(marketId, 'auto');

          // Validate fetched market data (defensive check for invalid blockchain state)
          // Sometimes events fire but market data is not yet fully written to RPC node
          if (!isValidMarket(market)) {
            if (attempt < retries) {
              logDEBUG('[RETRY]', `Market ${marketId} not ready (CancelledMarket, attempt ${attempt}/${retries}), retrying in ${delay}ms`, {
                marketId: market.id,
                creatorId: market.creatorId,
              });
              // Clear cache before retry to force fresh fetch
              this.clearMarketCache([marketId], 'retry fetch');
              await new Promise((resolve) => setTimeout(resolve, delay));
              delay = Math.min(delay * 2, 1000); // Exponential backoff, capped at 1s
              continue;
            } else {
              logDEBUG('[ERROR]', 'Market data still invalid after retries (CancelledMarket)', {
                marketId: market.id,
                creatorId: market.creatorId,
                attempts: retries,
              });
              return; // Give up after max retries
            }
          }

          // Success! Market data is valid
          listener(market);

          // Clear related caches in background after UI is updated (non-blocking)
          // Note: Market cache for this ID was already cleared before fetch
          // Cache clearing is cheap but we don't need to block the UI on it
          // Note: market.creatorId is already the creator address (not a numeric ID)
          this.clearCreatorCountsCache(market.creatorId); // live/pending decreased, voided increased
          this.clearCreatorActiveMarketsCountCache(market.creatorId); // Live badge count changed (live-1)
          this.clearCreatorCache(); // Homepage creator list
          return; // Exit retry loop on success
        }
      };

      // Start fetching with retry logic (non-blocking)
      fetchMarketWithRetry(cancelledMarketId.toString()).catch((err) => {
        if (this.shouldLogError(err)) {
          logDEBUG('[ERROR]', 'Error processing CancelledMarket event', err);
        }
      });
    };

    // Global creator event listeners - listen to ALL creator registrations/updates (not filtered by user)
    // This allows everyone's UI to update when ANY user registers/updates their creator profile
    this.creatorRegisteredListener = async (...args) => {
      // Deduplicate events
      if (!this.deduplicateEvent(BlockchainEvent.CREATED_CREATOR, args)) {
        return;
      }

      // Track event for connection quality monitoring
      this.wsLastEventTimestamp = Date.now();

      const creatorWallet = args[0] as string; // Event: CreatedCreator(wallet, creatorId, ...)

      // Validate event data (skip invalid creators)
      if (!this.isValidEventData(creatorWallet, undefined, 'CreatedCreator')) {
        return;
      }

      // Check if event is from current user - trigger user-specific callbacks
      const currentUserAddr = getCurrentUserAddress();
      if (currentUserAddr && creatorWallet.toLowerCase() === currentUserAddr.toLowerCase()) {
        triggerCreatorRegisteredCallbacks(); // Success popup for current user
        triggerBalanceRefreshWithDelay(); // Balance update
      }

      // Clear cache BEFORE fetch to force fresh data from RPC
      this.clearCreatorDetailsCache(creatorWallet); // Remove cached "not a creator" state
      this.clearCreatorCache(); // Homepage creator list
      this.clearHomePageStatsCache(); // Creator count increased

      // RACE CONDITION FIX: Retry fetching creator details if we get invalid data
      const fetchCreatorWithRetry = async (creatorWallet: string, retries = 5, delay = 300): Promise<void> => {
        for (let attempt = 1; attempt <= retries; attempt++) {
          try {
            const creator = await this.getCreatorDetails(creatorWallet, 'auto');

            // Validate fetched creator data
            if (!creator.id || creator.id === '0' || !creator.address || creator.address === ZERO_ADDRESS) {
              if (attempt < retries) {
                logDEBUG('[RETRY]', `Creator ${creatorWallet} not ready (CreatedCreator, attempt ${attempt}/${retries}), retrying...`);
                this.clearCreatorDetailsCache(creatorWallet);
                await new Promise((resolve) => setTimeout(resolve, delay));
                delay = Math.min(delay * 2, 1000); // Exponential backoff, capped at 1s
                continue;
              } else {
                logDEBUG('[ERROR]', 'Creator data still invalid after retries (CreatedCreator)', {
                  id: creator.id,
                  address: creator.address,
                });
                return;
              }
            }

            // Success! Creator data is valid
            logDEBUG('[SUCCESS] New creator registered:', {
              wallet: creatorWallet,
              creatorId: creator.id,
              nickname: creator.nickname,
              isCurrentUser: currentUserAddr?.toLowerCase() === creatorWallet.toLowerCase(),
            });

            // Force immediate cache flush before emitting event
            // This ensures HomePage gets fresh data when it refetches
            this.flushCacheClearBatch();

            // Emit event to notify UI components
            const events = useEvents();
            events.emit(EVENTS.CREATOR_REGISTERED, {
              creatorId: creator.id,
              creatorAddress: creatorWallet,
            });

            return;
          } catch (error) {
            if (attempt < retries && error instanceof Error && error.message.includes('not a registered creator')) {
              logDEBUG('[RETRY]', `Creator ${creatorWallet} not found yet (attempt ${attempt}/${retries}), retrying...`);
              await new Promise((resolve) => setTimeout(resolve, delay));
              delay = Math.min(delay * 2, 1000);
              continue;
            } else {
              logDEBUG('[ERROR]', 'Failed to fetch creator after retries', error);
              return;
            }
          }
        }
      };

      // Execute fetch with retry in background (non-blocking)
      fetchCreatorWithRetry(creatorWallet).catch((error) => {
        logError('EvmPredictionMarketProvider', 'unexpected error in fetchCreatorWithRetry', error);
      });
    };

    this.creatorUpdatedListener = async (...args) => {
      // Deduplicate events
      if (!this.deduplicateEvent(BlockchainEvent.UPDATED_CREATOR, args)) {
        return;
      }

      // Track event for connection quality monitoring
      this.wsLastEventTimestamp = Date.now();

      const creatorWallet = args[0] as string; // Event: UpdatedCreator(wallet, creatorId, ...)

      // Validate event data (skip invalid creators)
      if (!this.isValidEventData(creatorWallet, undefined, 'UpdatedCreator')) {
        return;
      }

      // Check if event is from current user - trigger user-specific callbacks
      const currentUserAddr = getCurrentUserAddress();
      if (currentUserAddr && creatorWallet.toLowerCase() === currentUserAddr.toLowerCase()) {
        triggerCreatorUpdatedCallbacks(); // Success popup for current user
        triggerBalanceRefreshWithDelay(); // Balance update
      }

      // Clear cache BEFORE fetch to force fresh data from RPC
      this.clearCreatorDetailsCache(creatorWallet); // Specific creator details
      this.clearCreatorCache(); // Homepage creator list (nickname/description changed)

      // RACE CONDITION FIX: Retry fetching creator details if we get invalid data
      const fetchCreatorWithRetry = async (creatorWallet: string, retries = 5, delay = 300): Promise<void> => {
        for (let attempt = 1; attempt <= retries; attempt++) {
          try {
            const creator = await this.getCreatorDetails(creatorWallet, 'auto');

            // Validate fetched creator data
            if (!creator.id || creator.id === '0' || !creator.address || creator.address === ZERO_ADDRESS) {
              if (attempt < retries) {
                logDEBUG('[RETRY]', `Creator ${creatorWallet} not ready (UpdatedCreator, attempt ${attempt}/${retries}), retrying...`);
                this.clearCreatorDetailsCache(creatorWallet);
                await new Promise((resolve) => setTimeout(resolve, delay));
                delay = Math.min(delay * 2, 1000); // Exponential backoff, capped at 1s
                continue;
              } else {
                logDEBUG('[ERROR]', 'Creator data still invalid after retries (UpdatedCreator)', {
                  id: creator.id,
                  address: creator.address,
                });
                return;
              }
            }

            // Success! Creator data is valid
            logDEBUG('[SUCCESS] Creator profile updated:', {
              wallet: creatorWallet,
              creatorId: creator.id,
              nickname: creator.nickname,
              isCurrentUser: currentUserAddr?.toLowerCase() === creatorWallet.toLowerCase(),
            });

            // Emit event to notify UI components to refresh creator data
            const events = useEvents();
            events.emit(EVENTS.CREATOR_UPDATED, {
              creatorId: creator.id,
              creatorAddress: creatorWallet,
            });

            return;
          } catch (error) {
            if (attempt < retries) {
              logDEBUG('[RETRY]', `Failed to fetch creator ${creatorWallet} (attempt ${attempt}/${retries}), retrying...`);
              await new Promise((resolve) => setTimeout(resolve, delay));
              delay = Math.min(delay * 2, 1000);
              continue;
            } else {
              logDEBUG('[ERROR]', 'Failed to fetch creator after retries', error);
              return;
            }
          }
        }
      };

      // Execute fetch with retry in background (non-blocking)
      fetchCreatorWithRetry(creatorWallet).catch((error) => {
        logError('EvmPredictionMarketProvider', 'unexpected error in fetchCreatorWithRetry', error);
      });
    };

    // Token event listeners (Approval, Transfer)
    // Note: Native currency transfers cannot be monitored - they don't emit blockchain events.
    // Block monitoring is too expensive for public RPC nodes. Use transaction confirmation instead.
    const wsTokenContract = await this.getWsTokenContract();

    this.tokenApprovedListener = async (...args) => {
      // DEFENSIVE: ethers.js event listeners receive (...eventArgs, event)
      // Approval(owner, spender, value) → args = [owner, spender, value, ContractEventPayload]
      // If args.length < 4, the event structure is malformed (likely from a different contract)
      if (args.length < 4) {
        // Silently skip - this is expected for non-standard ERC20 implementations
        // Our broad filter catches all Approval events, some may not match our ABI
        return;
      }

      const owner = args[0] as string; // Event: Approval(owner, spender, value)
      const spender = args[1] as string;
      const value = args[2] as bigint;

      // Skip invalid approvals (defensive check for zero addresses)
      if (!owner || !spender || value === undefined || owner === ZERO_ADDRESS || spender === ZERO_ADDRESS) {
        logWarn('EvmPredictionMarketProvider', 'skipping invalid Approval event', { owner, spender, value });
        return;
      }

      // Early return: only process approvals by the current user.
      // The subscription receives ALL Approval events on the token contract; on busy mainnet
      // chains this can be extremely high-volume, so we bail out before touching the dedup
      // cache or doing any further work for irrelevant events.
      const currentUserAddr = getCurrentUserAddress();
      if (!currentUserAddr || owner.toLowerCase() !== currentUserAddr.toLowerCase()) return;

      // Deduplicate events (pass full args — deduplicateEvent slices off the EventLog itself)
      if (!this.deduplicateEvent(BlockchainEvent.APPROVAL, args)) {
        return;
      }

      // Track event for connection quality monitoring
      this.wsLastEventTimestamp = Date.now();

      triggerTokenApprovedCallbacks(); // Success popup for current user
      triggerBalanceRefreshWithDelay(); // Balance + allowance update
      logDEBUG('[SUCCESS] Token approval detected for current user:', {
        owner,
        spender,
        value: formatUnits(value, 18),
      });
    };

    this.tokenTransferredListener = async (...args) => {
      // DEFENSIVE: ethers.js event listeners receive (...eventArgs, event)
      // Transfer(from, to, value) → args = [from, to, value, ContractEventPayload]
      // If args.length < 4, the event structure is malformed (likely from a different contract)
      if (args.length < 4) {
        // Silently skip - this is expected for non-standard ERC20 implementations
        // Our broad filter catches all Transfer events, some may not match our ABI
        return;
      }

      const from = args[0] as string; // Event: Transfer(from, to, value)
      const to = args[1] as string;
      const value = args[2] as bigint;

      // Skip invalid transfers (defensive check for zero addresses)
      if (!from || !to || value === undefined || from === ZERO_ADDRESS || to === ZERO_ADDRESS) {
        logWarn('EvmPredictionMarketProvider', 'skipping invalid Transfer event', { from, to, value });
        return;
      }

      // Early return: only process transfers involving the current user.
      // The subscription receives ALL Transfer events on the token contract; on busy mainnet
      // chains this can be extremely high-volume, so we bail out before touching the dedup
      // cache or doing any further work for irrelevant events.
      const currentUserAddr = getCurrentUserAddress();
      if (!currentUserAddr) return;
      const isFromUser = from.toLowerCase() === currentUserAddr.toLowerCase();
      const isToUser = to.toLowerCase() === currentUserAddr.toLowerCase();
      if (!isFromUser && !isToUser) return;

      // Deduplicate events (pass full args — deduplicateEvent slices off the EventLog itself)
      if (!this.deduplicateEvent(BlockchainEvent.TRANSFER, args)) {
        return;
      }

      // Track event for connection quality monitoring
      this.wsLastEventTimestamp = Date.now();

      triggerTokenTransferredCallbacks(); // Success popup for current user
      triggerBalanceRefreshWithDelay(); // Balance update
      logDEBUG('[SUCCESS] Token transfer detected for current user:', {
        from,
        to,
        value: formatUnits(value, 18),
        direction: isFromUser ? 'outgoing' : 'incoming',
      });
    };

    // Subscribe to HTTP provider events as FALLBACK (reliable but slower)
    // These create RPC filters that expire after 5min, causing harmless console noise
    // BUT they're critical for event detection when WebSocket is down!
    // Without these: No market notifications, no settlement alerts, broken UX when WS fails

    const httpSubscriptions = Promise.all([
      this.marketContract.on(createdMarketEvent, this.createdMarketListener),
      this.marketContract.on(settledMarketEvent, this.settledMarketListener),
      this.marketContract.on(placedPredictionEvent, this.placedPredictionListener),
      this.marketContract.on(cancelledMarketEvent, this.cancelledMarketListener),
      this.marketContract.on(createdCreatorEvent, this.creatorRegisteredListener),
      this.marketContract.on(updatedCreatorEvent, this.creatorUpdatedListener),
      ...(this.tokenContract && tokenApprovedEvent ? [this.tokenContract.on(tokenApprovedEvent, this.tokenApprovedListener)] : []),
      ...(this.tokenContract && tokenTransferredEvent ? [this.tokenContract.on(tokenTransferredEvent, this.tokenTransferredListener)] : []),
    ]);

    // Subscribe to WebSocket provider events (instant, ~100ms latency)
    // Deduplication ensures we don't process the same event twice
    let wsSubscriptions: Promise<void> | null = null;
    if (this.wsMarketContract && wsTokenContract) {
      // Use the same filters but on WebSocket contract
      const wsCreatedEvent = this.wsMarketContract.filters.CreatedMarket;
      const wsSettledEvent = this.wsMarketContract.filters.SettledMarket;
      const wsPlacedEvent = this.wsMarketContract.filters.PlacedPrediction;
      const wsCancelledEvent = this.wsMarketContract.filters.CancelledMarket;
      const wsCreatedCreatorEvent = this.wsMarketContract.filters.CreatedCreator;
      const wsUpdatedCreatorEvent = this.wsMarketContract.filters.UpdatedCreator;
      const wsApprovedEvent =
        currentUserAddrForFilter && this.wsTokenContract
          ? this.wsTokenContract.filters.Approval!(getAddress(currentUserAddrForFilter))
          : null;
      const wsTransferredEvent = IS_MAINNET ? null : this.wsTokenContract?.filters.Transfer;

      wsSubscriptions = Promise.all([
        this.wsMarketContract.on(wsCreatedEvent, this.createdMarketListener),
        this.wsMarketContract.on(wsSettledEvent, this.settledMarketListener),
        this.wsMarketContract.on(wsPlacedEvent, this.placedPredictionListener),
        this.wsMarketContract.on(wsCancelledEvent, this.cancelledMarketListener),
        this.wsMarketContract.on(wsCreatedCreatorEvent, this.creatorRegisteredListener),
        this.wsMarketContract.on(wsUpdatedCreatorEvent, this.creatorUpdatedListener),
        ...(this.wsTokenContract && wsApprovedEvent ? [this.wsTokenContract.on(wsApprovedEvent, this.tokenApprovedListener)] : []),
        ...(this.wsTokenContract && wsTransferredEvent ? [this.wsTokenContract.on(wsTransferredEvent, this.tokenTransferredListener)] : []),
      ])
        .then(() => {
          logDEBUG('[EvmPredictionMarketProvider] WebSocket event subscriptions active (instant updates)');
        })
        .catch((err) => {
          if (this.shouldLogError(err)) {
            logDEBUG('[WARN]', 'WebSocket subscription failed, using HTTP polling only', err);
          }
        });
    }

    // Wait for both HTTP and WebSocket subscriptions to complete
    await Promise.all([httpSubscriptions, wsSubscriptions].filter(Boolean));

    logDEBUG(
      '[EvmPredictionMarketProvider] Event subscriptions active:',
      this.wsMarketContract ? 'WebSocket (instant) + HTTP fallback (polling)' : 'HTTP polling only',
    );

    return createdMarketEvent;
  }

  /**
   * Deduplicate blockchain events to prevent processing the same event multiple times
   * This can happen when multiple subscriptions exist (e.g., useBlockchainEvents + provider)
   */
  private deduplicateEvent(eventName: string, args: unknown[]): boolean {
    // Exclude the last argument (EventLog object) as it differs between HTTP/WebSocket
    // Only hash the actual event parameters (e.g., wallet, marketId, etc.)
    const eventDataOnly = args.slice(0, -1);

    // Custom JSON serializer that handles BigInt
    const eventHash = `${eventName}-${JSON.stringify(eventDataOnly, (_key, value) => (typeof value === 'bigint' ? value.toString() : value))}`;

    if (this.recentEventHashes.has(eventHash)) {
      return false; // Duplicate detected
    }

    this.recentEventHashes.add(eventHash);
    setTimeout(() => this.recentEventHashes.delete(eventHash), this.EVENT_DEDUPLICATION_WINDOW_MS);
    return true; // Not a duplicate
  }

  async unsubscribeFromMarketUpdates(): Promise<void> {
    // Remove all event listeners from HTTP provider
    if (this.createdMarketListener) {
      await this.marketContract.off(this.marketContract.filters.CreatedMarket, this.createdMarketListener);
    }
    if (this.settledMarketListener) {
      await this.marketContract.off(this.marketContract.filters.SettledMarket, this.settledMarketListener);
    }
    if (this.placedPredictionListener) {
      await this.marketContract.off(this.marketContract.filters.PlacedPrediction, this.placedPredictionListener);
    }
    if (this.cancelledMarketListener) {
      await this.marketContract.off(this.marketContract.filters.CancelledMarket, this.cancelledMarketListener);
    }
    if (this.creatorRegisteredListener) {
      await this.marketContract.off(this.marketContract.filters.CreatedCreator, this.creatorRegisteredListener);
    }
    if (this.creatorUpdatedListener) {
      await this.marketContract.off(this.marketContract.filters.UpdatedCreator, this.creatorUpdatedListener);
    }
    // Remove all token event listeners from HTTP provider.
    // We use removeAllListeners() because the approval subscription uses a user-specific
    // topic filter (filters.Approval(userAddr)), and contract.off() requires the exact
    // same filter object that was used when subscribing.
    if (this.tokenContract) {
      this.tokenContract.removeAllListeners();
    }

    // Remove all event listeners from WebSocket provider if it exists
    if (this.wsMarketContract) {
      if (this.createdMarketListener) {
        await this.wsMarketContract.off(this.wsMarketContract.filters.CreatedMarket, this.createdMarketListener);
      }
      if (this.settledMarketListener) {
        await this.wsMarketContract.off(this.wsMarketContract.filters.SettledMarket, this.settledMarketListener);
      }
      if (this.placedPredictionListener) {
        await this.wsMarketContract.off(this.wsMarketContract.filters.PlacedPrediction, this.placedPredictionListener);
      }
      if (this.cancelledMarketListener) {
        await this.wsMarketContract.off(this.wsMarketContract.filters.CancelledMarket, this.cancelledMarketListener);
      }
      if (this.creatorRegisteredListener) {
        await this.wsMarketContract.off(this.wsMarketContract.filters.CreatedCreator, this.creatorRegisteredListener);
      }
      if (this.creatorUpdatedListener) {
        await this.wsMarketContract.off(this.wsMarketContract.filters.UpdatedCreator, this.creatorUpdatedListener);
      }
    }

    // Remove token event listeners from WebSocket provider if it exists
    if (this.wsTokenContract) {
      // Same reasoning as HTTP token cleanup above — use removeAllListeners() to handle
      // the user-specific topic filter used at subscription time.
      this.wsTokenContract.removeAllListeners();
    }

    // Clear stored listeners
    this.createdMarketListener = null;
    this.settledMarketListener = null;
    this.placedPredictionListener = null;
    this.cancelledMarketListener = null;
    this.creatorRegisteredListener = null;
    this.creatorUpdatedListener = null;
    this.tokenApprovedListener = null;
    this.tokenTransferredListener = null;
  }

  /**
   * Settle a market by declaring the winning outcome.
   *
   * Requirements:
   * - Caller must be the market creator
   * - Market must be past end time and not already settled
   * - Outcome ID must be valid (0 to N-1)
   *
   * Blockchain Events:
   * - Emits `SettledMarket(marketId, outcomeId, proof)`
   * - Triggers automatic cache invalidation
   * - Notifies subscribed listeners
   *
   * @param predictionId - Market ID to settle (string representation of uint256)
   * @param outcomeId - Winning outcome index (0-based)
   * @param proof - Optional proof URL/description (stored in blob storage)
   * @returns Promise<boolean> - True if transaction was mined successfully
   * @throws {Error} If not market creator or market already settled
   *
   * @example
   * ```typescript
   * // Settle market with proof
   * await provider.settleMarket('123', '1', 'https://example.com/proof');
   *
   * // Settle without proof
   * await provider.settleMarket('123', '0');
   * ```
   */
  async settleMarket(predictionId: string, outcomeId: string, proof?: string): Promise<boolean> {
    const marketWithSigner = await this.getMarketWithSigner();

    const predictionIdNum = Number(predictionId);
    const outcomeIdNum = Number(outcomeId);

    let proofData = ZERO_HASH;

    if (proof) {
      const proofDataBlobResponse = await createBlob(proof);
      proofData = '0x' + proofDataBlobResponse.id;
    }

    // Fix: incorrect data length (argument="proof", value="0x", code=INVALID_ARGUMENT, version=6.16.0)

    const tx = await marketWithSigner.settleMarket(toBigInt(predictionIdNum), toBigInt(outcomeIdNum), proofData);

    const receipt = await tx.wait();
    return receipt !== null;
  }

  /**
   * Place a prediction (bet) on a market outcome.
   *
   * Features:
   * - Automatic token approval if insufficient allowance
   * - Clears prediction count cache after successful prediction
   * - Amount is converted from human-readable to token decimals
   *
   * Requirements:
   * - Market must be active (not settled, not cancelled, before end time)
   * - Amount must be >= market's minimum prediction amount
   * - User must have sufficient token balance
   * - User must approve token allowance (done automatically)
   *
   * Blockchain Events:
   * - Emits `PlacedPrediction(userId, marketId, outcomeId, amount)`
   * - Triggers automatic cache invalidation
   * - Notifies subscribed listeners
   * - Updates user's balance and prediction history
   *
   * @param marketId - Market ID to predict on
   * @param outcomeId - Outcome index to bet on (0-based)
   * @param amount - Prediction amount in human-readable units (e.g., "10.5")
   * @param userId - User's Ethereum address
   * @param approvalAmount - Optional custom approval amount (defaults to amount)
   * @param minShares - Optional minimum shares expected (for slippage protection)
   * @returns Promise<boolean> - True if transaction was mined successfully
   * @throws {Error} If insufficient balance or market is not active
   *
   * @example
   * ```typescript
   * // Place 100 token bet on outcome 0
   * await provider.makePrediction('123', '0', '100', '0x1234...');
   *
   * // With custom approval amount
   * await provider.makePrediction('123', '1', '50', '0x1234...', '1000');
   * ```
   */
  async makePrediction(
    marketId: string,
    outcomeId: string,
    amount: string,
    userId: string,
    approvalAmount?: string,
    minShares?: string,
  ): Promise<boolean> {
    await this.checkAndApproveTokenAllowance(amount, userId, approvalAmount);

    const marketWithSigner = await this.getMarketWithSigner();

    const marketIdNum = Number(marketId);
    const outcomeIdNum = Number(outcomeId);
    const decimals = await this.getTokenDecimals();
    const amountInWei = parseUnits(amount, decimals);
    const minSharesBigInt = minShares ? toBigInt(minShares) : BigInt(0);

    const tx = await marketWithSigner.placePrediction(toBigInt(marketIdNum), toBigInt(outcomeIdNum), amountInWei, minSharesBigInt);

    const receipt = await tx.wait();

    // Clear prediction count cache since user made a new prediction
    this.clearPredictionCountCache(userId);

    return receipt !== null;
  }

  async getUserPredictionsBatch(userId: string, pages: number[]): Promise<Prediction[]> {
    const checksummed = this.ensureChecksummedAddress(userId);
    logDEBUG('getUserPredictionsBatch', checksummed, `pages: ${pages.join(',')}`);
    // Use multicall to fetch all requested pages in parallel
    const calls = pages.map((page) => {
      const callData = this.marketContract.interface.encodeFunctionData('getAllPredictionsForUser', [
        checksummed,
        toBigInt(PREDICTIONS_PAGE_SIZE),
        toBigInt(page),
      ]);
      return {
        target: this.MARKET_ADDRESS,
        allowFailure: true,
        callData,
      };
    });

    const results = await this.multicall3Contract.aggregate3.staticCall(calls);

    const pageToPredictionsMap: Record<number, LibMarket.PredictionStructOutput[]> = {};

    for (let i = 0; i < results.length; i++) {
      const result = results[i];
      const page = pages[i]!;

      if (result?.success) {
        const decodedData = this.marketContract.interface.decodeFunctionResult('getAllPredictionsForUser', result.returnData);
        pageToPredictionsMap[page] = decodedData[0] as LibMarket.PredictionStructOutput[];
      } else {
        logWarn('EvmPredictionMarketProvider', `failed to fetch predictions for user ${userId} page ${page}`);
        pageToPredictionsMap[page] = [];
      }
    }

    const predictions: Prediction[] = [];
    const decimals = await this.getTokenDecimals();

    for (const page of pages) {
      const predictionsData = pageToPredictionsMap[page] || [];

      for (let i = 0; i < predictionsData.length; i++) {
        const predictionData = predictionsData[i];

        if (!predictionData) {
          continue;
        }

        const marketIdStr = predictionData.marketId.toString();
        const outcomeId = predictionData.outcomeIndex.toString();

        const prediction: Prediction = {
          id: predictionData.predictionId.toString(),
          marketId: marketIdStr,
          userId: predictionData.wallet,
          outcomeId,
          amount: Number(formatUnits(predictionData.amount, decimals)),
          shares: predictionData.shares,
          createdAt: Number(predictionData.createdAt) * 1000,
        };

        predictions.push(prediction);
      }
    }

    return predictions;
  }

  async getUserPredictionsCount(userId: string): Promise<number> {
    const checksummed = this.ensureChecksummedAddress(userId);

    // Check cache first
    const cached = this.predictionCountCache.get(checksummed);
    const now = Date.now();

    if (cached && now - cached.timestamp < this.PREDICTION_COUNT_CACHE_TTL) {
      logDEBUG('getUserPredictionsCount', 'CACHED', cached.count);
      logCacheHit('predictionCount');
      return cached.count;
    }

    // Check if request is already in flight
    const inFlightRequest = this.inFlightUserPredictionsCountRequests.get(checksummed);
    if (inFlightRequest) {
      const currentCount = this.inFlightUserPredictionsCountCallerCounts.get(checksummed) || 1;
      this.inFlightUserPredictionsCountCallerCounts.set(checksummed, currentCount + 1);
      return inFlightRequest;
    }

    // Cache miss or expired - fetch from contract
    logDEBUG('getUserPredictionsCount', checksummed);
    logCacheMiss('predictionCount');
    this.inFlightUserPredictionsCountCallerCounts.set(checksummed, 1);

    const fetchPromise = (async () => {
      try {
        const totalPredictions = await this.marketContract.predictionsCountPerUser(checksummed);
        const count = Number(totalPredictions);

        // Update cache
        this.predictionCountCache.set(checksummed, { count, timestamp: Date.now() });

        return count;
      } finally {
        const callerCount = this.inFlightUserPredictionsCountCallerCounts.get(checksummed) || 1;
        if (callerCount > 1) {
          logDEBUG(`[IN-FLIGHT DEDUP]`, `${callerCount} concurrent callers reused request for user predictions count: ${checksummed}`);
        }
        this.inFlightUserPredictionsCountRequests.delete(checksummed);
        this.inFlightUserPredictionsCountCallerCounts.delete(checksummed);
      }
    })();

    this.inFlightUserPredictionsCountRequests.set(checksummed, fetchPromise);
    return fetchPromise;
  }

  /**
   * Clear the predictions count cache for a specific user or all users
   * Should be called after transactions that change prediction counts (makePrediction)
   */
  clearPredictionCountCache(userId?: string): void {
    if (userId) {
      logDEBUG('[CACHE CLEAR]', 'Prediction count cache for user:', userId);
      this.predictionCountCache.delete(userId);
    } else {
      logDEBUG('[CACHE CLEAR]', 'ALL prediction count cache');
      this.predictionCountCache.clear();
    }
  }

  /**
   * Set a market in the cache with LRU eviction
   * Automatically removes least recently used entries when max size is reached
   */
  private setMarketCache(marketId: string, market: Market, timestamp: number): void {
    // If at max capacity, remove the oldest entry (first in Map = least recently used)
    if (this.marketCache.size >= this.MARKET_CACHE_MAX_SIZE) {
      const firstKey = this.marketCache.keys().next().value;
      if (firstKey) {
        this.marketCache.delete(firstKey);
        logCacheEviction('market');
      }
    }
    // Add new entry (goes to end of Map = most recently used)
    this.marketCache.set(marketId, { market, timestamp });
  }

  /**
   * Clear the market cache for specific market IDs or all markets
   * Should be called after market data changes (settlement, void, market update events)
   */
  clearMarketCache(marketIds?: string[], source?: string): void {
    if (marketIds) {
      logDEBUG('[CACHE CLEAR]', 'Market cache for IDs:', marketIds, source ? `(${source})` : '');
      for (const marketId of marketIds) {
        this.marketCache.delete(marketId);
      }
    } else {
      logDEBUG('[CACHE CLEAR]', 'ALL market cache', source ? `(${source})` : '');
      this.marketCache.clear();
    }
  }

  /**
   * Clear the creator cache
   * Should be called after creator data changes (new market created, account switch)
   */
  clearCreatorCache(): void {
    logDEBUG('[CACHE CLEAR]', 'Creator cache (all pages)');
    this.creatorCache.clear();
  }

  /**
   * Clear the homepage stats cache
   * Should be called after events that change the total counts (new market, new prediction, new creator)
   * Batched to avoid duplicate logging within the same tick
   */
  clearHomePageStatsCache(): void {
    this.pendingHomePageStatsClear = true;
    this.scheduleCacheClearBatch();
  }

  clearCreatorStatsCache(creatorAddress?: string): void {
    if (creatorAddress) {
      // Batch clear to avoid duplicate logging within the same tick
      const checksummed = this.ensureChecksummedAddress(creatorAddress);
      this.pendingCreatorStatsClearSet.add(checksummed);
      this.scheduleCacheClearBatch();
    } else {
      // Clear all creator stats cache immediately (rare operation)
      logDEBUG('[CACHE CLEAR]', 'ALL creator stats cache');
      this.creatorStatsCache.clear();
    }
  }

  /**
   * Schedule a batched cache clear to execute on the next microtask
   * Multiple calls within the same tick are coalesced into a single execution
   */
  private scheduleCacheClearBatch(): void {
    if (this.cacheClearBatchTimeout !== null) {
      return; // Already scheduled
    }

    this.cacheClearBatchTimeout = window.setTimeout(() => {
      this.flushCacheClearBatch();
    }, 0);
  }

  /**
   * Execute all pending batched cache clears
   */
  private flushCacheClearBatch(): void {
    this.cacheClearBatchTimeout = null;

    // Clear creator stats
    if (this.pendingCreatorStatsClearSet.size > 0) {
      const addresses = Array.from(this.pendingCreatorStatsClearSet);
      if (addresses.length === 1) {
        logDEBUG('[CACHE CLEAR]', 'Creator stats for:', addresses[0]);
      } else {
        logDEBUG('[CACHE CLEAR]', `Creator stats for ${addresses.length} creators`);
      }
      addresses.forEach((addr) => this.creatorStatsCache.delete(addr));
      this.pendingCreatorStatsClearSet.clear();
    }

    // Clear creator counts
    if (this.pendingCreatorCountsClearSet.size > 0) {
      const addresses = Array.from(this.pendingCreatorCountsClearSet);
      if (addresses.length === 1) {
        logDEBUG('[CACHE CLEAR]', 'Creator counts for:', addresses[0]);
      } else {
        logDEBUG('[CACHE CLEAR]', `Creator counts for ${addresses.length} creators`);
      }
      addresses.forEach((addr) => this.creatorCountsCache.delete(addr));
      this.pendingCreatorCountsClearSet.clear();
    }

    // Clear homepage stats
    if (this.pendingHomePageStatsClear) {
      logDEBUG('[CACHE CLEAR]', 'Homepage stats cache');
      this.homePageStatsCache = {};
      this.pendingHomePageStatsClear = false;
    }
  }

  clearCreatorDetailsCache(creatorAddress?: string): void {
    if (creatorAddress) {
      // Clear specific creator's details cache AND in-flight requests
      const checksummed = this.ensureChecksummedAddress(creatorAddress);
      logDEBUG('[CACHE CLEAR]', 'Creator details for:', checksummed);
      this.creatorDetailsCache.delete(checksummed);
      // Also clear any in-flight requests to force fresh fetch
      this.inFlightCreatorDetailsRequests.delete(checksummed);
      this.inFlightCreatorDetailsCallerCounts.delete(checksummed);
    } else {
      // Clear all creator details cache AND in-flight requests
      logDEBUG('[CACHE CLEAR]', 'ALL creator details cache');
      this.creatorDetailsCache.clear();
      this.inFlightCreatorDetailsRequests.clear();
      this.inFlightCreatorDetailsCallerCounts.clear();
    }
  }

  clearCreatorAddressByIdCache(creatorId?: string): void {
    if (creatorId) {
      // Clear specific creator ID mapping
      logDEBUG('[CACHE CLEAR]', 'Creator address by ID for:', creatorId);
      this.creatorAddressByIdCache.delete(creatorId);
    } else {
      // Clear all creator ID mappings (should rarely be needed since permanent)
      logDEBUG('[CACHE CLEAR]', 'ALL creator address by ID cache');
      this.creatorAddressByIdCache.clear();
    }
  }

  clearCreatorCountsCache(creatorAddress?: string): void {
    if (creatorAddress) {
      // Batch clear to avoid duplicate logging within the same tick
      const checksummed = this.ensureChecksummedAddress(creatorAddress);
      this.pendingCreatorCountsClearSet.add(checksummed);
      this.scheduleCacheClearBatch();
    } else {
      // Clear all creator counts cache immediately (rare operation)
      logDEBUG('[CACHE CLEAR]', 'ALL creator counts cache');
      this.creatorCountsCache.clear();
    }
  }

  clearCreatorActiveMarketsCountCache(creatorAddress?: string): void {
    if (creatorAddress) {
      const checksummed = this.ensureChecksummedAddress(creatorAddress);
      logDEBUG('[CACHE CLEAR]', 'Creator active markets count cache for:', checksummed);
      this.creatorActiveMarketsCountCache.delete(checksummed);
    } else {
      // Clear all creator active markets count cache (used by batch API)
      logDEBUG('[CACHE CLEAR]', 'ALL creator active markets count cache');
      this.creatorActiveMarketsCountCache.clear();
    }
  }

  async voidMarket(marketId: string): Promise<boolean> {
    const marketWithSigner = await this.getMarketWithSigner();

    const marketIdNum = Number(marketId);

    const tx = await marketWithSigner.cancelMarket(toBigInt(marketIdNum));

    const receipt = await tx.wait();
    return receipt !== null;
  }

  private async getMarketWithSigner(): Promise<FlmFunCore> {
    const accounts = useAccounts();

    if (!accounts.selectedAccount.value) {
      throw new Error('No account selected');
    }

    if (accounts.selectedAccount.value.providerId !== ACCOUNT_PROVIDER_ID.REOWN_EVM_ACCOUNT_PROVIDER) {
      throw new Error('Selected account is not an EVM account');
    }

    const address = accounts.selectedAccount.value.address;
    const provider = (await accounts.getProviderById(ACCOUNT_PROVIDER_ID.REOWN_EVM_ACCOUNT_PROVIDER)) as ReownEvmAccountProvider;

    // Ensure user is on correct network before any transaction
    await provider.ensureCorrectNetwork();

    const signer = await provider.getAccountSigner(address);
    return this.marketContract.connect(signer);
  }

  private async convertRawMarketToMarket(rawMarket: LibMarket.MarketStructOutput, blobCache?: Map<string, string>): Promise<Market> {
    const createdAt = new Date(Number(rawMarket.startTime) * 1000);
    const deadline = new Date(Number(rawMarket.endTime) * 1000);
    const status = Number(rawMarket.status);
    const isSettled = status === 2;
    const isVoid = status === 1;

    const title = ethers.decodeBytes32String(rawMarket.title);

    // Get token decimals once for all conversions
    const decimals = await this.getTokenDecimals();

    // Get virtual liquidity multiplier from cached config
    const config = await this.getContractConfig();
    const virtualLiquidityMultiplier = config.virtualLiquidityMultiplier;

    const totalVolume = rawMarket.totalPerOutcome.reduce((sum, volume) => sum.plus(formatUnits(volume, decimals)), new BigNumber(0));
    const totalVirtualVolume = new BigNumber(formatUnits(rawMarket.minPrediction, decimals)).multipliedBy(virtualLiquidityMultiplier);

    const outcomes: Outcome[] = rawMarket.outcomeDetails.map((outcome: SmartContractOutcomeDetails, index: number) => {
      const volumeRaw = rawMarket.totalPerOutcome[index];
      if (volumeRaw === undefined) {
        throw new Error('Volume data missing for outcome index ' + index);
      }
      const volume = Number(formatUnits(volumeRaw, decimals));

      const probRaw = rawMarket.probBpsPerOutcome[index];
      if (probRaw === undefined) {
        throw new Error('Probability data missing for outcome index ' + index);
      }
      const probBps = probRaw.toString();

      const virtualVolumeOnPrediction = totalVirtualVolume.multipliedBy(probBps).dividedBy(BASIS_POINTS_DIVISOR);
      const volumeOnPrediction = new BigNumber(volume).plus(virtualVolumeOnPrediction);
      const odds = totalVolume.plus(totalVirtualVolume).dividedBy(volumeOnPrediction);
      const chance = volumeOnPrediction.dividedBy(totalVolume.plus(totalVirtualVolume));

      const outcomeItem: Outcome = {
        id: index.toString(),
        name: ethers.decodeBytes32String(outcome.title),
        probability: new BigNumber(probBps).dividedBy(100).toNumber(),
        odds: odds.toNumber(),
        chance: chance.toNumber(),
        volume,
      };

      return outcomeItem;
    });

    const winningOutcomeId = isSettled ? String(rawMarket.winningOutcomeIndex) : null;

    const rulesBlobId = rawMarket.description.slice(2);
    let rules = '';
    if (rulesBlobId !== '') {
      // Use pre-fetched blob from cache if available, otherwise fetch individually
      if (blobCache && blobCache.has(rulesBlobId)) {
        rules = blobCache.get(rulesBlobId) || '';
        if (rules === '') {
          rules = 'Important: Failed to load rules! Rules may still apply but they could not be loaded.';
        }
      } else if (blobCache && blobCache.size === 0) {
        // Empty cache explicitly signals lazy loading - skip blob fetch for faster market display
        // Rules will be empty and loaded on-demand when user clicks "View Rules"
        rules = '';
      } else {
        try {
          const rulesBlobResponse = await readBlob(rulesBlobId);
          rules = rulesBlobResponse ? rulesBlobResponse : '';
        } catch (error) {
          logWarn('EvmPredictionMarketProvider', `error fetching rules blob for market ${rawMarket.id.toString()}`, error);
          rules = 'Important: Failed to load rules! Rules may still apply but they could not be loaded.';
        }
      }
    }

    const userIdToPredictions: Record<string, PredictionWithMarket['prediction'][]> = {};

    for (let i = 0; i < rawMarket.stakeDetails.length; i++) {
      const stakeDetail = rawMarket.stakeDetails[i]!;
      const userId = stakeDetail.wallet.toLowerCase();
      const outcomeId = stakeDetail.outcomeIndex.toString();
      const amount = Number(formatUnits(stakeDetail.amount, decimals));
      const shares = stakeDetail.shares;

      const prediction: PredictionWithMarket['prediction'] = {
        id: `${rawMarket.id}-${userId.toLowerCase()}-${outcomeId}-${i}`,
        marketId: rawMarket.id.toString(),
        userId,
        outcomeId,
        amount,
        shares,
        createdAt: 0,
      };

      if (!userIdToPredictions[userId]) {
        userIdToPredictions[userId] = [];
      }
      userIdToPredictions[userId].push(prediction);
    }

    // Extract totalsPerOutcome and totalSharesPerOutcome from contract
    const totalsPerOutcome: Record<string, number> = {};
    const totalSharesPerOutcome: Record<string, bigint> = {};
    for (let i = 0; i < rawMarket.totalPerOutcome.length; i++) {
      const outcomeId = i.toString();
      totalsPerOutcome[outcomeId] = Number(formatUnits(rawMarket.totalPerOutcome[i]!, decimals));
      totalSharesPerOutcome[outcomeId] = rawMarket.totalSharesPerOutcome[i]!;
    }

    // Parse creator fees total (0 for unsettled markets)
    const creatorFeesTotal = Number(formatUnits(rawMarket.creatorFeesTotal, decimals));

    return {
      id: rawMarket.id.toString(),
      creatorId: rawMarket.creator, // Keep checksummed format from contract to prevent ENS resolution
      title,
      rules,
      marketType: Number(rawMarket.marketType) as MarketType,
      deadline,
      createdAt,
      completedAt: rawMarket.completedAt > 0n ? new Date(Number(rawMarket.completedAt) * 1000) : undefined,
      isSettled,
      isVoid,
      outcomes,
      winningOutcomeId,
      userIdToPredictions,
      totalPredictionsCount: Number(rawMarket.totalPredictionsCount), // Use pre-computed count from contract
      minPredictionAmount: rawMarket.minPrediction,
      feeBps: rawMarket.feeBps,
      totalsPerOutcome,
      totalSharesPerOutcome,
      creatorFeesTotal,
    };
  }

  private async checkAndApproveTokenAllowance(amount: string, userId: string, approvalAmount?: string): Promise<void> {
    const decimals = await this.getTokenDecimals();
    const amountInWei = parseUnits(amount, decimals);
    const tokenContract = await this.getTokenContract();

    const allowance: bigint = await tokenContract.allowance(userId, this.MARKET_ADDRESS);

    if (allowance >= amountInWei) {
      return;
    }

    const accounts = useAccounts();

    if (!accounts.selectedAccount.value) {
      throw new Error('No account selected');
    }

    if (accounts.selectedAccount.value.providerId !== ACCOUNT_PROVIDER_ID.REOWN_EVM_ACCOUNT_PROVIDER) {
      throw new Error('Selected account is not an EVM account');
    }

    const provider = (await accounts.getProviderById(ACCOUNT_PROVIDER_ID.REOWN_EVM_ACCOUNT_PROVIDER)) as ReownEvmAccountProvider;

    // Ensure user is on correct network before token approval
    await provider.ensureCorrectNetwork();

    const signer = await provider.getAccountSigner(userId);
    const tokenWithSigner = tokenContract.connect(signer) as ERC20Contract;

    // Use the specified approval amount or default to 10k
    const approvalAmountInWei = approvalAmount ? parseUnits(approvalAmount, decimals) : getDefaultApprovalAmount(decimals);

    const tx = await tokenWithSigner.approve(this.MARKET_ADDRESS, approvalAmountInWei);
    const receipt = await tx.wait();
    if (!receipt) {
      throw new Error('Token approval transaction failed');
    }
  }

  async approveToken(amount: string, userId: string): Promise<void> {
    // Approve the MAXIMUM of (required amount, default 10k) for optimal UX
    // This ensures large predictions work while still providing buffer for smaller ones
    const requiredAmount = parseFloat(amount);
    const defaultAmount = parseFloat(DEFAULT_APPROVAL_AMOUNT_STRING);
    const approvalAmount = Math.max(requiredAmount, defaultAmount).toString();

    await this.checkAndApproveTokenAllowance(approvalAmount, userId, approvalAmount);
  }

  async getMinCreatorFees(): Promise<number> {
    const minFees = await this.marketContract.minFees();
    return Number(minFees);
  }

  async getMaxCreatorFees(): Promise<number> {
    const maxFees = await this.marketContract.maxFees();
    return Number(maxFees);
  }

  async getMinPredictionAmount(): Promise<bigint> {
    const minPrediction = await this.marketContract.minPrediction();
    return minPrediction;
  }

  async getTokenBalance(userAddress: string): Promise<bigint> {
    const tokenContract = await this.getTokenContract();
    const balance = await tokenContract.balanceOf(userAddress);
    return balance;
  }

  async getTokenAllowance(userAddress: string): Promise<bigint> {
    const tokenContract = await this.getTokenContract();
    const allowance = await tokenContract.allowance(userAddress, this.MARKET_ADDRESS);
    return allowance;
  }

  /**
   * Batch fetch token balance, allowance, and native balance using Multicall3 for efficiency
   * Reduces 3 RPC calls to 1 RPC call
   */
  async getTokenBalanceAndAllowance(userAddress: string): Promise<{ balance: bigint; allowance: bigint; nativeBalance: bigint }> {
    const tokenContract = await this.getTokenContract();
    const tokenAddress = tokenContract.target as string;

    const calls = [
      {
        target: tokenAddress,
        allowFailure: false,
        callData: tokenContract.interface.encodeFunctionData('balanceOf', [userAddress]),
      },
      {
        target: tokenAddress,
        allowFailure: false,
        callData: tokenContract.interface.encodeFunctionData('allowance', [userAddress, this.MARKET_ADDRESS]),
      },
      {
        target: MULTICALL3_ADDRESS,
        allowFailure: false,
        callData: this.multicall3Contract.interface.encodeFunctionData('getEthBalance', [userAddress]),
      },
    ];

    const results = await this.multicall3Contract.aggregate3.staticCall(calls);

    const balanceResult = tokenContract.interface.decodeFunctionResult('balanceOf', results[0]!.returnData);
    const allowanceResult = tokenContract.interface.decodeFunctionResult('allowance', results[1]!.returnData);
    const nativeBalanceResult = this.multicall3Contract.interface.decodeFunctionResult('getEthBalance', results[2]!.returnData);

    return {
      balance: balanceResult[0] as bigint,
      allowance: allowanceResult[0] as bigint,
      nativeBalance: nativeBalanceResult[0] as bigint,
    };
  }

  async setTokenAllowance(amount: bigint): Promise<boolean> {
    const accounts = useAccounts();

    if (!accounts.selectedAccount.value) {
      throw new Error('No account selected');
    }

    if (accounts.selectedAccount.value.providerId !== ACCOUNT_PROVIDER_ID.REOWN_EVM_ACCOUNT_PROVIDER) {
      throw new Error('Selected account is not an EVM account');
    }

    const userId = accounts.selectedAccount.value.address;
    const provider = (await accounts.getProviderById(ACCOUNT_PROVIDER_ID.REOWN_EVM_ACCOUNT_PROVIDER)) as ReownEvmAccountProvider;

    // Ensure user is on correct network before token allowance transaction
    await provider.ensureCorrectNetwork();

    const signer = await provider.getAccountSigner(userId);
    const tokenContract = await this.getTokenContract();
    const tokenWithSigner = tokenContract.connect(signer) as ERC20Contract;

    const tx = await tokenWithSigner.approve(this.MARKET_ADDRESS, amount);
    const receipt = await tx.wait();
    return receipt !== null;
  }

  /**
   * Transfer ERC20 tokens to a recipient
   * @param to Recipient address
   * @param amount Amount in token units (e.g., "10.5")
   * @returns Transaction hash
   */
  async transferToken(to: string, amount: string): Promise<string> {
    const accounts = useAccounts();

    if (!accounts.selectedAccount.value) {
      throw new Error('No account selected');
    }

    if (accounts.selectedAccount.value.providerId !== ACCOUNT_PROVIDER_ID.REOWN_EVM_ACCOUNT_PROVIDER) {
      throw new Error('Selected account is not an EVM account');
    }

    const userId = accounts.selectedAccount.value.address;
    const provider = (await accounts.getProviderById(ACCOUNT_PROVIDER_ID.REOWN_EVM_ACCOUNT_PROVIDER)) as ReownEvmAccountProvider;

    // Ensure user is on correct network before token transfer
    await provider.ensureCorrectNetwork();

    const signer = await provider.getAccountSigner(userId);
    const tokenContract = await this.getTokenContract();
    const decimals = await this.getTokenDecimals();

    const amountInWei = parseUnits(amount.toString(), decimals);
    const tokenWithSigner = tokenContract.connect(signer) as ERC20Contract;

    const tx = await tokenWithSigner.transfer(to, amountInWei);

    // Return transaction hash immediately for instant feedback via WebSocket events
    return tx.hash;
  }

  /**
   * Transfer native currency to a recipient
   * @param to Recipient address
   * @param amount Amount in native currency (e.g., "0.01")
   * @returns Transaction hash
   */
  async transferNative(to: string, amount: string): Promise<string> {
    const accounts = useAccounts();

    if (!accounts.selectedAccount.value) {
      throw new Error('No account selected');
    }

    if (accounts.selectedAccount.value.providerId !== ACCOUNT_PROVIDER_ID.REOWN_EVM_ACCOUNT_PROVIDER) {
      throw new Error('Selected account is not an EVM account');
    }

    const userId = accounts.selectedAccount.value.address;
    const provider = (await accounts.getProviderById(ACCOUNT_PROVIDER_ID.REOWN_EVM_ACCOUNT_PROVIDER)) as ReownEvmAccountProvider;

    // Ensure user is on correct network before native transfer
    await provider.ensureCorrectNetwork();

    const signer = await provider.getAccountSigner(userId);

    const amountInWei = parseEther(amount.toString());

    const tx = await signer.sendTransaction({
      to: to,
      value: amountInWei,
    });

    // Return transaction hash immediately for instant feedback via WebSocket events
    return tx.hash;
  }

  /**
   * Batch fetch all contract configuration values using Multicall3 for efficiency
   * Includes caching with 1 day TTL
   * @returns Object containing all configuration values
   */
  async getContractConfig(): Promise<ContractConfig> {
    // Check if cache is valid
    const now = Date.now();
    if (this.contractConfigCache.data && now - this.contractConfigCache.timestamp < this.CONFIG_CACHE_TTL) {
      logDEBUG('getContractConfig', 'CACHED');
      logCacheHit('contractConfig');
      return this.contractConfigCache.data;
    }

    // Check if request is already in flight
    if (this.inFlightContractConfigRequest) {
      this.inFlightContractConfigCallerCount++;
      return this.inFlightContractConfigRequest;
    }

    logDEBUG('getContractConfig', 'fetching...');
    logCacheMiss('contractConfig');
    this.inFlightContractConfigCallerCount = 1;

    // Create the fetch promise
    const fetchPromise = (async () => {
      try {
        // Prepare multicall calls for all config values
        const calls = [
          {
            target: this.MARKET_ADDRESS,
            allowFailure: false,
            callData: this.marketContract.interface.encodeFunctionData('maxTime'),
          },
          {
            target: this.MARKET_ADDRESS,
            allowFailure: false,
            callData: this.marketContract.interface.encodeFunctionData('platformFees'),
          },
          {
            target: this.MARKET_ADDRESS,
            allowFailure: false,
            callData: this.marketContract.interface.encodeFunctionData('minFees'),
          },
          {
            target: this.MARKET_ADDRESS,
            allowFailure: false,
            callData: this.marketContract.interface.encodeFunctionData('maxFees'),
          },
          {
            target: this.MARKET_ADDRESS,
            allowFailure: false,
            callData: this.marketContract.interface.encodeFunctionData('minPrediction'),
          },
          {
            target: this.MARKET_ADDRESS,
            allowFailure: false,
            callData: this.marketContract.interface.encodeFunctionData('predictionCurrency'),
          },
          {
            target: this.MARKET_ADDRESS,
            allowFailure: false,
            callData: this.marketContract.interface.encodeFunctionData('affiliateFeePercentage'),
          },
          {
            target: this.MARKET_ADDRESS,
            allowFailure: false,
            callData: this.marketContract.interface.encodeFunctionData('lowestPossibleOdds'),
          },
          {
            target: this.MARKET_ADDRESS,
            allowFailure: false,
            callData: this.marketContract.interface.encodeFunctionData('virtualLiquidityMultiplier'),
          },
        ];

        // Execute multicall
        const results = await this.multicall3Contract.aggregate3.staticCall(calls);

        // Decode results
        const maxTimeResult = this.marketContract.interface.decodeFunctionResult('maxTime', results[0]!.returnData);
        const platformFeesResult = this.marketContract.interface.decodeFunctionResult('platformFees', results[1]!.returnData);
        const minFeesResult = this.marketContract.interface.decodeFunctionResult('minFees', results[2]!.returnData);
        const maxFeesResult = this.marketContract.interface.decodeFunctionResult('maxFees', results[3]!.returnData);
        const minPredictionResult = this.marketContract.interface.decodeFunctionResult('minPrediction', results[4]!.returnData);
        const predictionCurrencyResult = this.marketContract.interface.decodeFunctionResult('predictionCurrency', results[5]!.returnData);
        const affiliateFeePercentageResult = this.marketContract.interface.decodeFunctionResult(
          'affiliateFeePercentage',
          results[6]!.returnData,
        );
        const lowestPossibleOddsResult = this.marketContract.interface.decodeFunctionResult('lowestPossibleOdds', results[7]!.returnData);
        const virtualLiquidityMultiplierResult = this.marketContract.interface.decodeFunctionResult(
          'virtualLiquidityMultiplier',
          results[8]!.returnData,
        );

        const config = {
          maxTime: Number(maxTimeResult[0]),
          platformFees: platformFeesResult[0] as bigint,
          minCreatorFees: Number(minFeesResult[0]),
          maxCreatorFees: Number(maxFeesResult[0]),
          minPredictionAmount: minPredictionResult[0] as bigint,
          predictionCurrency: predictionCurrencyResult[0] as string,
          affiliateFeePercentage: Number(affiliateFeePercentageResult[0]),
          lowestPossibleOdds: lowestPossibleOddsResult[0] as bigint,
          virtualLiquidityMultiplier: Number(virtualLiquidityMultiplierResult[0]),
        };

        // Update cache
        this.contractConfigCache = {
          data: config,
          timestamp: Date.now(),
        };

        return config;
      } finally {
        const callerCount = this.inFlightContractConfigCallerCount;
        if (callerCount > 1) {
          logDEBUG(`[IN-FLIGHT DEDUP]`, `${callerCount} concurrent callers reused request for contract config`);
        }
        this.inFlightContractConfigRequest = null;
        this.inFlightContractConfigCallerCount = 0;
      }
    })();

    this.inFlightContractConfigRequest = fetchPromise;
    return fetchPromise;
  }

  /**
   * Get the prediction currency symbol from the cached config
   */
  async getPredictionCurrencySymbol(): Promise<string> {
    if (EvmPredictionMarketProvider.tokenSymbolCache !== null) {
      logDEBUG('getPredictionCurrencySymbol', 'CACHED', EvmPredictionMarketProvider.tokenSymbolCache);
      logCacheHit('tokenSymbol');
      return EvmPredictionMarketProvider.tokenSymbolCache;
    }

    logDEBUG('getPredictionCurrencySymbol', 'fetching...');
    logCacheMiss('tokenSymbol');
    const config = await this.getContractConfig();

    // Fetch symbol from the token contract
    try {
      const tokenContract = new Contract(config.predictionCurrency, ERC20_ABI, this.provider) as ERC20Contract;
      const symbol = await tokenContract.symbol();
      EvmPredictionMarketProvider.tokenSymbolCache = symbol;
      return symbol;
    } catch (error) {
      logWarn('EvmPredictionMarketProvider', 'failed to fetch token symbol, defaulting to address', error);
      const fallback = config.predictionCurrency.slice(0, 6) + '...';
      EvmPredictionMarketProvider.tokenSymbolCache = fallback;
      return fallback;
    }
  }

  /**
   * Clear all caches (useful for network switch or account change)
   * This ensures fresh data is fetched from the new network/account
   */
  clearAllCaches(): void {
    logDEBUG('[CACHE CLEAR]', 'clearing all caches (network switch or forced refresh)');

    // Clear all instance-level caches
    this.marketCache.clear();
    this.creatorCache.clear();
    this.creatorDetailsCache.clear();
    this.creatorAddressByIdCache.clear();
    this.creatorCountsCache.clear();
    this.creatorActiveMarketsCountCache.clear(); // Used by batch API
    this.creatorStatsCache.clear();
    this.predictionCountCache.clear();

    // Clear singleton caches (static/permanent)
    EvmPredictionMarketProvider.tokenDecimalsCache = null;
    EvmPredictionMarketProvider.tokenSymbolCache = null;

    // Clear contract config cache
    this.contractConfigCache = { data: null, timestamp: 0 };

    // Clear homepage stats cache
    this.homePageStatsCache = {
      creatorsCount: undefined,
      marketsCount: undefined,
      predictionsCount: undefined,
    };
  }

  /**
   * Warm up critical caches on app startup for better UX
   * Uses multicall for efficiency and runs as fire-and-forget
   * Caches warmed:
   * 1. Contract Config (Infinity TTL)
   * 2. Token Decimals (Infinity TTL)
   * 3. Token Symbol (Infinity TTL)
   * 4. Homepage Stats (5min TTL)
   */
  async warmCache(): Promise<void> {
    try {
      logDEBUG('[CACHE WARM]', 'Warming critical caches...');

      // Prepare multicall for contract config + token info
      const calls = [
        // Contract config calls (9 values)
        {
          target: this.MARKET_ADDRESS,
          allowFailure: false,
          callData: this.marketContract.interface.encodeFunctionData('maxTime'),
        },
        {
          target: this.MARKET_ADDRESS,
          allowFailure: false,
          callData: this.marketContract.interface.encodeFunctionData('platformFees'),
        },
        {
          target: this.MARKET_ADDRESS,
          allowFailure: false,
          callData: this.marketContract.interface.encodeFunctionData('minFees'),
        },
        {
          target: this.MARKET_ADDRESS,
          allowFailure: false,
          callData: this.marketContract.interface.encodeFunctionData('maxFees'),
        },
        {
          target: this.MARKET_ADDRESS,
          allowFailure: false,
          callData: this.marketContract.interface.encodeFunctionData('minPrediction'),
        },
        {
          target: this.MARKET_ADDRESS,
          allowFailure: false,
          callData: this.marketContract.interface.encodeFunctionData('predictionCurrency'),
        },
        {
          target: this.MARKET_ADDRESS,
          allowFailure: false,
          callData: this.marketContract.interface.encodeFunctionData('affiliateFeePercentage'),
        },
        {
          target: this.MARKET_ADDRESS,
          allowFailure: false,
          callData: this.marketContract.interface.encodeFunctionData('lowestPossibleOdds'),
        },
        {
          target: this.MARKET_ADDRESS,
          allowFailure: false,
          callData: this.marketContract.interface.encodeFunctionData('virtualLiquidityMultiplier'),
        },
        // Homepage stats (3 values)
        {
          target: this.MARKET_ADDRESS,
          allowFailure: false,
          callData: this.marketContract.interface.encodeFunctionData('creatorsCount'),
        },
        {
          target: this.MARKET_ADDRESS,
          allowFailure: false,
          callData: this.marketContract.interface.encodeFunctionData('marketsCount'),
        },
        {
          target: this.MARKET_ADDRESS,
          allowFailure: false,
          callData: this.marketContract.interface.encodeFunctionData('predictionsCount'),
        },
      ];

      // Execute multicall
      const results = await this.multicall3Contract.aggregate3.staticCall(calls);

      // Decode and cache contract config
      const maxTimeResult = this.marketContract.interface.decodeFunctionResult('maxTime', results[0]!.returnData);
      const platformFeesResult = this.marketContract.interface.decodeFunctionResult('platformFees', results[1]!.returnData);
      const minFeesResult = this.marketContract.interface.decodeFunctionResult('minFees', results[2]!.returnData);
      const maxFeesResult = this.marketContract.interface.decodeFunctionResult('maxFees', results[3]!.returnData);
      const minPredictionResult = this.marketContract.interface.decodeFunctionResult('minPrediction', results[4]!.returnData);
      const predictionCurrencyResult = this.marketContract.interface.decodeFunctionResult('predictionCurrency', results[5]!.returnData);
      const affiliateFeePercentageResult = this.marketContract.interface.decodeFunctionResult(
        'affiliateFeePercentage',
        results[6]!.returnData,
      );
      const lowestPossibleOddsResult = this.marketContract.interface.decodeFunctionResult('lowestPossibleOdds', results[7]!.returnData);
      const virtualLiquidityMultiplierResult = this.marketContract.interface.decodeFunctionResult(
        'virtualLiquidityMultiplier',
        results[8]!.returnData,
      );

      const config = {
        maxTime: Number(maxTimeResult[0]),
        platformFees: platformFeesResult[0] as bigint,
        minCreatorFees: Number(minFeesResult[0]),
        maxCreatorFees: Number(maxFeesResult[0]),
        minPredictionAmount: minPredictionResult[0] as bigint,
        predictionCurrency: predictionCurrencyResult[0] as string,
        affiliateFeePercentage: Number(affiliateFeePercentageResult[0]),
        lowestPossibleOdds: lowestPossibleOddsResult[0] as bigint,
        virtualLiquidityMultiplier: Number(virtualLiquidityMultiplierResult[0]),
      };

      // Cache contract config
      this.contractConfigCache = {
        data: config,
        timestamp: Date.now(),
      };

      // Decode and cache homepage stats
      const creatorsCountResult = this.marketContract.interface.decodeFunctionResult('creatorsCount', results[9]!.returnData);
      const marketsCountResult = this.marketContract.interface.decodeFunctionResult('marketsCount', results[10]!.returnData);
      const predictionsCountResult = this.marketContract.interface.decodeFunctionResult('predictionsCount', results[11]!.returnData);

      this.homePageStatsCache = {
        creatorsCount: { count: Number(creatorsCountResult[0]), timestamp: Date.now() },
        marketsCount: { count: Number(marketsCountResult[0]), timestamp: Date.now() },
        predictionsCount: { count: Number(predictionsCountResult[0]), timestamp: Date.now() },
      };

      // Fetch token decimals and symbol separately (needs token contract address from config)
      const tokenContract = new Contract(config.predictionCurrency, ERC20_ABI, this.provider) as ERC20Contract;
      const [decimals, symbol] = await Promise.all([tokenContract.decimals(), tokenContract.symbol()]);

      EvmPredictionMarketProvider.tokenDecimalsCache = Number(decimals);
      EvmPredictionMarketProvider.tokenSymbolCache = symbol;

      logDEBUG('[CACHE WARM]', '✓ Successfully warmed caches:', {
        contractConfig: '8 values',
        homePageStats: '3 values',
        tokenDecimals: decimals,
        tokenSymbol: symbol,
      });
    } catch (error) {
      logWarn('EvmPredictionMarketProvider', '[CACHE WARM] failed to warm caches (will lazy load)', error);
      // Don't throw - cache warming is optional optimization
    }
  }
}

export default EvmPredictionMarketProvider;
